/**
 * The workflow template domain types — the shape the API returns for a published
 * workflow, plus the pure helpers that read it.
 *
 * These live here rather than beside the axios client that fetches them because
 * `PdfFunction` (ADR-0031) fills PDFs against this exact graph inside a Lambda,
 * where there is no axios and no browser. `api/workflows.ts` re-exports every
 * name below, so front-end callers import them from the same place they always
 * did — this move is invisible to them.
 */

export type WorkflowType = 'InternalAdvisor' | 'ExternalAdvisor' | 'Both';
export type TargetPlatform = 'APEX' | 'IFDS';
export type WorkflowStatus = 'Draft' | 'Pending' | 'Approved' | 'Rejected';

export type WorkflowStepType =
  | 'Welcome'
  | 'DynamicForm'
  | 'AdditionalDocuments'
  | 'PdfGeneration'
  /**
   * RETIRED 2026-08-08 — merged into 'PdfGeneration', which now owns the PDF
   * source templates as well as generating the filled output. The BE folds
   * legacy rows on read, so this should never arrive over the wire; kept in the
   * union so a stale bundle still type-checks against older data.
   */
  | 'PdfFileUpload'
  | 'OneSpanESign'
  | 'ApplicationReview'
  /**
   * Terminal confirmation card (2026-09-04). Platform-managed singleton, always
   * last, and the one step the runtime renders with no navigation at all.
   */
  | 'ThankYou';

export type WorkflowStep = {
  id: string;
  type: WorkflowStepType;
  titleEn: string;
  titleFr: string;
  /**
   * Short label for the surfaces that LIST the step instead of rendering it — the runtime's
   * left rail and the Application Review recap. Null / absent / blank = both fall back to
   * {@link titleEn} / {@link titleFr}, which is every step authored before this existed.
   *
   * Never resolve this inline — call {@link stepNavTitle}, which owns the per-language
   * fallback. `titleEn`/`titleFr` remain the step's own PAGE heading (the `h2` an
   * AdditionalDocuments / OneSpanESign step renders); on a DynamicForm step that heading comes
   * from the first SECTION instead, which is why the step title there only ever reached the rail.
   */
  navTitleEn?: string | null;
  navTitleFr?: string | null;
  descriptionEn: string;
  descriptionFr: string;
  /**
   * PDF source templates the originator uploaded. Only populated on the
   * 'PdfGeneration' step (was 'PdfFileUpload' before the 2026-08-08 merge).
   * Prefer {@link stepsWithPdfTemplates} over testing the step type — it keys
   * off the data, so it can't drift with the type rename.
   */
  fileEntries: FileEntry[];
  /**
   * Originator-defined document slots the advisor uploads at runtime. Only
   * meaningful when type === 'AdditionalDocuments'; empty for every other type.
   */
  additionalDocumentEntries: AdditionalDocumentEntry[];
  /**
   * File extensions (lowercase, no leading dot) accepted into the unnamed
   * "optional documents" bucket — a single advisor-facing dropzone, separate
   * from additionalDocumentEntries, that takes any number of files of these
   * types with no per-file name. Only meaningful when type ===
   * 'AdditionalDocuments'; empty for every other type.
   */
  optionalDocumentExtensions: string[];
  /**
   * Combined file-size cap (megabytes) for the optional-documents bucket.
   * Between {@link MIN_DOCUMENT_MAX_FILE_SIZE_MB} and {@link
   * MAX_DOCUMENT_MAX_FILE_SIZE_MB}. Only meaningful when type ===
   * 'AdditionalDocuments'.
   */
  optionalDocumentMaxFileSizeMb: number;
  /**
   * Whether the optional-documents bucket is shown to the advisor (and in the
   * originator's preview, which renders the same runtime step). Only
   * meaningful when type === 'AdditionalDocuments'; defaults to true.
   */
  showOptionalDocumentsSection: boolean;
  /**
   * Combined size cap (MB) across every file uploaded into this step's REQUIRED
   * named entries. `null` / absent = no combined cap, which is how every step
   * authored before this setting existed behaves — a silent retro-default would
   * have made an already-published workflow with three required 3 MB documents
   * unfillable mid-flow.
   *
   * Enforced at pick time in `AdditionalDocumentsStep` (see `useSizeBudget`);
   * independent of, and additional to, each entry's own per-file
   * {@link AdditionalDocumentEntry.maxFileSizeMb}.
   */
  mandatoryCombinedMaxSizeMb?: number | null;
  /**
   * Combined size cap (MB) across every file in the unnamed optional-documents
   * bucket. Same null semantics and the same enforcement point as
   * {@link mandatoryCombinedMaxSizeMb}.
   */
  optionalCombinedMaxSizeMb?: number | null;
  /**
   * Most documents the advisor may attach to the optional bucket. Replaces the
   * 10 that used to be hard-coded into the advisor runtime, so the default is
   * exactly that number and nothing changes for a step nobody configures.
   */
  maxOptionalDocuments?: number | null;
  /**
   * Form builder canvas — sections containing rows of columns, each column holds
   * 0 or 1 field. Only populated when type === 'DynamicForm'; empty for every
   * other type. Section order, row order within a section, and within-row column
   * order are all canonical render orders at runtime.
   */
  sections: FormSection[];
  // Step-level visibility gating. Source fields live in PRIOR steps only —
  // scope enforced by the FE picker; the runtime evaluator decides at render
  // time and the BE refuses self-references. Empty list = always visible.
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
  // Step-level "Disable if" — the INVERSE polarity of `conditions` above: when
  // these MATCH, every section and field on the step renders inert. The step
  // stays visible and navigable and its answers still reach the PDF and BMO,
  // which is the whole point of the rule (a hidden step's answers are dropped
  // — spec §6.4 / `visibleValues.ts`). Evaluate only via `isDisabledByRule`.
  // Empty / absent = never disabled. Only meaningful on type 'DynamicForm'.
  disableConditionCombinator?: 'all' | 'any' | null;
  disableConditions?: FileEntryCondition[];
  /**
   * Withhold this step from the PUBLIC customer runtime (`/ext/…`) — spec §12's
   * per-step "advisor only" checkbox, for a step whose questions only an advisor
   * can answer (or that only BMO needs). The advisor runtime and the
   * originator/approver previews are unaffected.
   *
   * Audience gating, NOT a condition: it is fixed for the life of the runtime
   * rather than flipping with the answers, so it is read in exactly one place —
   * {@link isPublicHiddenStep} — and every downstream computation inherits it
   * from there. Absent/false on every step authored before this flag existed,
   * which is the "visible to everyone" default.
   *
   * Only meaningful on type === 'DynamicForm'; the domain refuses it on any
   * other type (the rest are either structural or already public-hidden).
   */
  advisorOnly?: boolean | null;
  /**
   * Originator-authored signing config — only meaningful on type === 'OneSpanESign'.
   * Null on every other type, and null on a fresh OneSpanESign step the originator
   * hasn't configured yet. See `OneSpanConfig` below for shape.
   */
  oneSpanConfig?: OneSpanConfig | null;
  /**
   * Landing-card content — only meaningful on type === 'Welcome'. Null until the
   * originator saves the config modal; the runtime falls back to i18n defaults +
   * live PDF entry labels while null.
   */
  welcomeConfig?: WelcomeConfig | null;
  /**
   * Confirmation-card content — only meaningful on type === 'ThankYou'. Null
   * until the originator saves the config modal; the runtime falls back to the
   * workflow title while null.
   */
  thankYouConfig?: ThankYouConfig | null;
  /**
   * "Answer this way and the rest of the workflow becomes a different form."
   *
   * The INLINE sibling of {@link SelectOption.redirectTemplateId}: a redirect
   * pops a modal and navigates away to a brand-new application, abandoning the
   * answers already given, while a branch keeps the application — its
   * `clientAppId`, its appName, its title — and swaps only the content below
   * this step. One application, two bodies of content.
   *
   * Empty / absent on every step nobody configured, which is the "this workflow
   * runs start to finish" default. Only meaningful on type === 'DynamicForm';
   * the domain refuses it on any other type.
   */
  formBranches?: StepFormBranch[] | null;
};

/**
 * One branch rule: the radio answer that triggers a swap, and what to swap in.
 *
 * Deliberately NOT a {@link FileEntryCondition}. A condition answers "is this
 * visible", is evaluated everywhere, and composes with combinators; a branch
 * answers "which form am I filling", fires once, and has to stay resolvable
 * from the rule ALONE on resume — where the triggering answer is no longer in
 * the saved values, because only the selected form's values are persisted.
 * Sharing the condition shape would have bought nothing and hidden that.
 *
 * By convention a workflow declares exactly ONE of these: the BMO save contract
 * records a swap as the single `appInfo.subsequentApplication` boolean, so a
 * second rule leaves resume with no way to tell which form was being filled.
 * Nothing enforces that — per product decision we let it break rather than
 * build cycle and uniqueness checks the originators would have to work around.
 */
export type StepFormBranch = {
  id: string;
  /** A RadioGroup field on THIS step. */
  fieldId: string;
  /** The answer that triggers the swap — a {@link SelectOption.value}. */
  optionValue: string;
  /** The workflow whose steps replace every step below this one. */
  targetTemplateId: string;
  /**
   * Merge the target's first DynamicForm step's sections onto the END of this
   * step, instead of letting it arrive as the next step. Lets a branch open
   * with its first questions on the page the advisor is already looking at.
   */
  appendFirstStep: boolean;
};

/**
 * Welcome step config — the landing card the advisor sees first. All text is
 * manually entered per language; `pdfListEn`/`pdfListFr` are free-text bullet
 * lines (prefilled in the modal from the workflow's PDF entries, then owned by
 * the originator). `showLanguageSelector` toggles the English/French radio that
 * gates the runtime language.
 */
export type WelcomeConfig = {
  titleEn: string;
  titleFr: string;
  subheaderEn: string;
  subheaderFr: string;
  descriptionEn: string;
  descriptionFr: string;
  showLanguageSelector: boolean;
  pdfListEn: string[];
  pdfListFr: string[];
  /** Opt-in: no EDIT on the Introduction recap on Application Review (QA RIF TC-12-01). */
  hideEditInReview?: boolean;
};

/**
 * Thank You step config — the product name interpolated into the confirmation
 * headline ("your {product} Application Package …").
 *
 * Only the product name is authored, not the sentence around it: that copy forks
 * by audience (advisor vs public customer) and both variants live together in the
 * i18n bundle.
 *
 * Absent = never configured, and the runtime falls back to the workflow's own
 * title for the active language. `productTitleFr` may be empty on a saved config
 * — French then falls back on its own, with no cross-copying from English.
 */
export type ThankYouConfig = {
  productTitleEn: string;
  productTitleFr: string;
};

/**
 * E-Signature step config. Drives the SIGNATORY payload BMO sends to OneSpan —
 * we never call OneSpan ourselves, so this is purely an authoring artifact:
 * which signers exist, what fields populate their contact info, and where each
 * signer signs inside each PDF.
 */
export type OneSpanConfig = {
  roles: SignatoryRole[];
  documents: SignatureDocument[];
};

export type SignatoryRole = {
  /** Lowercase slug the originator types; wire keys (`id_{slug}` / `r_{slug}`)
   *  are derived at payload-build time. */
  slug: string;
  type: SignatoryType;
  /** 1-based signing order. */
  index: number;
  /**
   * Required when `type === 'applicant'` (their info lives in the form);
   * must be null when `type === 'advisor'` (their info comes from the BFF
   * session at runtime).
   */
  fieldMappings: SignatoryFieldMappings | null;
  /**
   * Originator-typed display name shown wherever this signer is rendered
   * (e.g. the runtime preview tag). Required when `type === 'applicant'`;
   * null when `type === 'advisor'` (always shown as "Advisor").
   */
  labelEn: string | null;
  labelFr: string | null;
  // Gating conditions — does this signer get included in the signing
  // package. Same shape/semantics as step/section/field conditions. Empty
  // list = always included. Only meaningful for `type === 'applicant'`.
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
  /**
   * Pins this role to exactly ONE instance of the repeatable section its
   * mapped fields live in (0-based, matches `RosterEntry.instanceIdx` /
   * `qualifiedKey`), instead of auto-multiplying into one signer per
   * instance. Lets an originator author separate roles ("Account Holder 1",
   * "Account Holder 2") that each get their own signature placement — a
   * role that auto-multiplies only ever gets ONE physical signature box
   * (bound to its first instance; see `BmoDataBuilder.BuildESignInfo`).
   * Null (the default) preserves today's auto-multiply behavior and is
   * meaningless when the role's mapped fields aren't in a repeatable
   * section.
   */
  fixedInstanceIndex?: number | null;
  /**
   * Whether this party is expected to SIGN.
   *
   * An application's `applicantInfo` is populated from these roles, but not
   * everyone named on an application signs it — a beneficiary or a secondary
   * contact is a real party BMO wants identified who has nothing to put a pen
   * to. `false` says exactly that: the role still resolves contact details and
   * still reaches BMO as an applicant, but it gets no signature placements, no
   * anchors, and no entry in `eSignInfo.signerInfo`.
   *
   * Absent/null means TRUE — every role authored before the toggle existed
   * signs, and only an explicit `false` opts out. Read through
   * `roleExpectsSignature` rather than compared directly, so the default lives
   * in one place. Always true for the advisor (the domain refuses otherwise):
   * the role only exists at all once a box is placed on them.
   */
  requiresSignature?: boolean | null;
};

export type SignatoryType = 'applicant' | 'advisor';

export type SignatoryFieldMappings = {
  /** Required at submit time. May be Guid.Empty during draft state. */
  nameFieldId: string;
  /** Optional — many BMO forms collect a single "Full Name" field where last
   *  name lives inside the name string. Guid.Empty when not provided. */
  lastNameFieldId: string;
  emailFieldId: string;
  phoneFieldId: string;
  /**
   * Optional — the RadioGroup / SingleSelect field whose answer decides THIS
   * signer's `eSignInfo.signerInfo[].language` on the BMO save payload, when
   * the form asks the client their preferred language directly. Guid.Empty when
   * not provided, which is the norm.
   *
   * The field's stored option VALUE is what is read, not its label: anything
   * starting `en` or `fr` (case-insensitive) wins, and everything else — no
   * mapping, unanswered, or an unrecognized value — falls back to the
   * application language. Normalization is deliberately server-side only
   * (`BmoDataBuilder`), so the browser never decides the wire format.
   *
   * Applicant-only in practice: the advisor role carries no field mappings at
   * all, so their invitation always follows the application language.
   */
  languageFieldId?: string | null;
};

export type SignatureDocument = {
  /** Reference to a `StepFileEntry.id` on a PdfFileUpload step in the same workflow. */
  pdfEntryId: string;
  fields: SignatureField[];
};

/**
 * Where one signature (or its companion date label) sits on a page, in **PDF
 * points with a bottom-left origin** — pdf-lib's native space, so stamping the
 * anchor into the generated PDF is a direct `drawText({ x, y })` with no
 * conversion. The originator authors it by dragging a box over a pdf.js-rendered
 * page; `originator/pdfPlacementGeometry.ts` owns both directions of that
 * conversion.
 *
 * None of this reaches BMO. `docs/save-application_v7.yaml` gives `fields[]` no
 * geometry member — the platform stamps the white anchor text itself and BMO's
 * OneSpan call finds it by string. Geometry is purely how we decide where to
 * put that string.
 */
export type SignaturePlacement = {
  /** 0-based page index. */
  page: number;
  /** PDF points from the page's left edge. */
  x: number;
  /** PDF points from the page's bottom edge. */
  y: number;
  width: number;
  height: number;
};

/**
 * How many date boxes one signature may carry.
 *
 * Nothing in the anchor scheme breaks past this — the `label<i>` counter is
 * unbounded — so the cap exists only to keep a stray double-click from turning
 * into a pile of overlapping boxes nobody meant to author. Mirrored in
 * `WorkflowStep.MaxDatePlacementsPerSignature`, which is where it is enforced;
 * the placement page just stops offering the button.
 */
export const MAX_DATE_PLACEMENTS_PER_SIGNATURE = 5;

/**
 * How many input boxes one signature may carry.
 *
 * The same cap as dates, for the same reason and with the same absence of a
 * technical limit behind it — the `Input<n>` counter is unbounded. Mirrored in
 * `WorkflowStep.MaxInputPlacementsPerSignature`, which is where it is enforced;
 * the placement page just stops offering the button.
 */
export const MAX_INPUT_PLACEMENTS_PER_SIGNATURE = 5;

export type SignatureField = {
  /** Auto-incremented within a doc: `sig_field_1`, `sig_field_2`, … */
  name: string;
  /** Auto-incremented within a doc: `field_1`, `field_2`, … Rewritten at BMO
   *  payload-build time into the brace-wrapped `{signerId.anchor}` form the
   *  eSignInfo contract requires (spec §5.4). */
  binding: string;
  /** Literal string in the PDF — OneSpan anchors the signature widget by
   *  finding it. Always square-bracket wrapped (`[SignatorySig0]`); the
   *  brackets are part of the anchor, so the PDF's white text carries them too.
   *
   *  GENERATED from the signatory's display name + a per-document index
   *  (`originator/signatureAnchors.ts`), never typed. The platform stamps this
   *  exact literal into the generated PDF, so the drawer and the document can no
   *  longer disagree — which is what they silently did while this was hand-typed
   *  on both sides. */
  textAnchor: string;
  /** Matches one of the parent config's role slugs. */
  role: string;
  /**
   * Where this signature sits in the EN / FR variant of the PDF.
   *
   * **Independent, not mirrored.** The two files are separate uploads with
   * different layouts, so a box placed on the EN page says nothing about where
   * it belongs on the FR one. An application generates exactly one language, and
   * `BmoDataBuilder` emits only that language's fields — so a signature placed
   * in one language and not the other simply does not exist for applications in
   * the other. `EnsureSubmittable` refuses that asymmetry before an approver
   * ever sees it.
   *
   * Null on both when the config predates drag-and-drop placement: those rows
   * carry a hand-typed anchor against a PDF the originator tagged themselves, so
   * they stamp nothing and behave exactly as they always did.
   */
  enPlacement: SignaturePlacement | null;
  frPlacement: SignaturePlacement | null;
  /**
   * Companion date labels — where OneSpan stamps the signing date. Per
   * signature, not per signer: only present where the PDF actually has a date
   * area next to that signature (spec §5.4:204). Each entry is emitted as an
   * `INPUT`/`LABEL` entry directly after its signature in `documents[].fields`.
   *
   * A signature can carry SEVERAL dates — a form that wants the same signature
   * dated beside the signature line and again in a summary block. The anchors
   * are told apart by the per-signer `label<i>` counter the two halves of the
   * contract already maintain (`[Anchor.label0.Date]`, `[Anchor.label1.Date]` —
   * spec §5.4:232), so **order in this array is meaningful**: it is what
   * `collectAnchorStamps` and `BmoDataBuilder` both walk to assign those
   * indexes, and they must walk it identically.
   *
   * Empty — not null — when the signature has no date at all, which is every
   * config authored before dates existed. Independent per language for the same
   * reason `enPlacement` / `frPlacement` are, and a date only counts where its
   * signature is placed in the same language.
   */
  enDatePlacements: SignaturePlacement[];
  frDatePlacements: SignaturePlacement[];
  /**
   * How OneSpan should render that date: the format token string
   * (`YYYY/MM/DD`, or anything the originator types) and its point size.
   *
   * **Shared by both languages**, unlike the placements — these describe the
   * date itself rather than where it sits, and a date that prints one way in the
   * EN PDF and another in the FR one is a difference nobody asked for.
   *
   * They also decide the date box's footprint: it is sized to these tokens
   * measured at this point size (`originator/dateFormatMetrics.ts`), so the box
   * on screen is the text that will appear rather than an arbitrary rectangle.
   *
   * Null until the originator opens the date box's settings — and null reaches
   * BMO as an omitted member, not a default, so configs authored before these
   * existed send exactly what they always sent.
   */
  dateFormat: string | null;
  dateFontSize: number | null;
  /**
   * Gating conditions on the signature itself — the same "show conditionally"
   * the originator has on fields, sections, steps, PDF entries and signer roles,
   * authored through the same `ConditionsEditorModal`.
   *
   * An unmet condition removes the signature from BOTH halves of the
   * e-signature contract: `collectAnchorStamps` does not stamp its anchor into
   * the generated PDF, and `BmoDataBuilder.BuildDocuments` does not describe it
   * to BMO. The two must agree — a signature described but not stamped is one
   * OneSpan cannot find — so both read `signatureConditions.ts` rather than
   * evaluating anything themselves.
   *
   * **Shared by both languages**, like `dateFormat`: a condition says *whether*
   * the signature is wanted, not where it sits. Empty list plus a null
   * combinator — the state of every signature authored before this existed —
   * means "always signed".
   */
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
  /**
   * Whether this placement collects INITIALS rather than a full signature.
   *
   * The only thing it changes on the wire is the `subtype` `BmoDataBuilder`
   * emits — `SIGNATURE`/`INITIALS` instead of `SIGNATURE`/`CAPTURE` — so OneSpan
   * renders an initials widget where this anchor is found. Everything else about
   * the field is identical: same anchor, same binding, same optional date label,
   * same gating, same white text stamped into the PDF.
   *
   * **Shared by both languages**, like `dateFormat` and `conditions`: it says
   * *what* is collected, not where it sits, and a box that takes a signature in
   * the EN PDF and initials in the FR one is a difference nobody asked for.
   *
   * Absent/false on every placement authored before initials existed — which is
   * exactly the "full signature" they all were.
   */
  isInitials?: boolean;
  /**
   * Companion **text boxes the signer types into** — OneSpan's `INPUT`/`TEXT`,
   * for a decline reason or an explanatory note beside the thing being signed.
   *
   * A COMPANION OF THIS SIGNATURE, exactly as a date label is: it anchors off
   * the signature's own anchor (`[AdvisorSig1.AdvisorInput0]`), it is dropped
   * from the package when the signature is, and it disappears with it. Inputs
   * were free-standing placements of their own when they first shipped
   * (2026-09-23); BMO's `textAnchor` is a path into the signature above it, so
   * they are not.
   *
   * A signature can carry SEVERAL — a decline reason beside the signature line
   * and a note in a summary block. They are told apart by a per-signer
   * `Input<n>` counter that both halves of the contract maintain, so **order in
   * this array is meaningful**: it is what `collectAnchorStamps` and
   * `BmoDataBuilder` both walk to assign those indexes, and they must walk it
   * identically.
   *
   * Empty — not null — when the signature has no input box, which is every
   * signature authored before these existed. Independent per language for the
   * same reason `enDatePlacements` / `frDatePlacements` are, and an input only
   * counts where its signature is placed in the same language.
   */
  enInputPlacements: SignaturePlacement[];
  frInputPlacements: SignaturePlacement[];
  /**
   * Which OneSpan input widget this signature's input boxes are. Null for a
   * signature that carries none.
   *
   * `TEXTFIELD` (single-line) is what the placement page authors and what BMO's
   * pipeline takes; the other two members of the union are superseded spellings
   * that still load and still save. See `InputSubtype`.
   *
   * **Shared by this signature's inputs**, like `dateFormat` is shared by its
   * dates: it describes what the box collects rather than where it sits, and
   * therefore shared by both languages too.
   */
  inputSubtype?: InputSubtype | null;
  /**
   * The rules OneSpan enforces on what the signer types. Null — the state of a
   * signature whose input settings were never opened — reaches BMO as a block
   * of platform defaults rather than an omitted member, because BMO's pipeline
   * expects a complete `validation` on every input field
   * (`ESignFieldValidation.ForInput`).
   *
   * Shared by this signature's inputs and by both languages, for the same
   * reason `inputSubtype` is.
   */
  inputValidation?: InputValidation | null;
};

/**
 * The OneSpan input widgets a box may be. `'TEXTFIELD'` is the confirmed one —
 * the only value written since 2026-09-30, and the only one BMO ever receives.
 *
 * `'TEXTAREA'` is what the first cut of input boxes wrote (2026-09-23), copied
 * from BMO's hand-authored probe package, and `'TEXT'` is what replaced it
 * (2026-09-24) on a reading of that package BMO has since corrected. Both stay
 * in the union so a workflow authored in either window still type-checks when
 * it is loaded; nothing writes them any more, and `BmoDataBuilder` normalises
 * both to `'TEXTFIELD'` on the way to BMO, so there is no migration.
 */
export type InputSubtype = 'TEXTFIELD' | 'TEXT' | 'TEXTAREA';

/**
 * The upper bound OneSpan accepts on an input's length, and the default the
 * settings form starts at. Mirrored in `WorkflowStep.MaxInputMaxLength`, which
 * is where it is enforced.
 */
export const MAX_INPUT_LENGTH = 4000;

/**
 * What OneSpan enforces on an input box's contents.
 *
 * Every member is optional *in storage* — a box whose settings were never
 * opened holds none of them — but the payload BMO receives always carries a
 * complete `validation` block: `ESignFieldValidation.ForInput` fills each unset
 * member from the platform default (required, 0–`MAX_INPUT_LENGTH`, no pattern).
 * `pattern` is free text — a regex OneSpan evaluates, deliberately not
 * validated for contents here for the same reason the date format never was.
 *
 * There is deliberately no `errorMessage`: it was a single hardcoded, untranslated
 * "Invalid input" on every box until 2026-10-30, and OneSpan's own message at
 * least follows the package's locale. Dropped rather than left unsent, so a
 * future settings panel cannot collect one that silently never ships.
 */
export type InputValidation = {
  required?: boolean | null;
  minLength?: number | null;
  maxLength?: number | null;
  pattern?: string | null;
};

export type OneSpanConfigPayload = OneSpanConfig;

export type FormSection = {
  id: string;
  titleEn: string;
  titleFr: string;
  rows: FormRow[];
  // Visibility conditions on the section — when gated, every field inside
  // hides until the rule matches. Same shape as PDF/field conditions.
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  // Section-level "Disable if" — when these MATCH, every field inside renders
  // inert (and Add another / Remove are withdrawn), but the section stays
  // visible and its answers still ship. Inverse polarity of `conditions`;
  // evaluate only via `isDisabledByRule`. Empty / absent = never disabled.
  disableConditionCombinator?: 'all' | 'any' | null;
  disableConditions?: FileEntryCondition[];
  // §1 Repeatable Sections. When true, the advisor sees an "Add Another"
  // button below the section and can spawn multiple instances of it.
  // Each instance's values live under keys of the form
  // `inst:<sectionId>:<index>:<fieldId>` in the values map. Instance
  // count for a repeatable section is stored under
  // `__instanceCount:<sectionId>` (default 1 when unset).
  repeatable?: boolean;
  // Advisor-side floor on the number of instances. Always >= 1. The runtime
  // seeds this many starter instances and refuses to remove below it. Only
  // meaningful when `repeatable` is true; defaults to 1 when unset.
  minInstances?: number;
  // Advisor-side cap on the number of instances. Null = unlimited.
  maxInstances?: number | null;
  // Bilingual header pattern shown above each instance. Supports a `{n}`
  // placeholder that gets replaced with the 1-based instance index at
  // runtime ("Account Holder {n}" → "Account Holder 1", etc.). Null =
  // renderer falls back to "<titleEn> {n}" / "<titleFr> {n}".
  instanceLabelEn?: string | null;
  instanceLabelFr?: string | null;
  // Application Review recaps this section WITHOUT its Edit link — for answers
  // the advisor must not revisit from the review (RDSP's Eligibility gate).
  // Presentation only. Absent / false = the Edit link shows as before.
  hideEditInReview?: boolean;
  // "Repeat once per instance of" (RDSP QA D-39) — id of another repeatable
  // section whose instance count this repeatable section mirrors: one block
  // per account holder. FormShell keeps `__instanceCount:<this>` equal to the
  // source's count, removing source instance k removes instance k here too,
  // and the section renders no Add / Remove of its own. Null / absent = an
  // ordinary section.
  repeatWithSectionId?: string | null;
  /** Repeatable only: "Add another" is offered only while these hold (RESP QA TC-08-05). Empty / absent = always. */
  addConditions?: FileEntryCondition[] | null;
  addConditionCombinator?: 'all' | 'any' | null;
  /**
   * Repeatable only: AcroForm boxes that print HOW MANY instances this section
   * holds — "2" in the "number of accountholders" slot. 1-to-many across
   * documents, exactly like a field's `pdfMappings`.
   *
   * Lives on the SECTION rather than on a field because that is what it counts.
   * Hanging it on some field inside the section would make the handle vanish
   * the day that field is deleted, silently — and a count box that quietly
   * stops printing is the failure this shape exists to avoid.
   *
   * Authored only through the PDF mapper, which synthesises one row per
   * repeatable section. There is no builder control and no advisor-visible
   * field: the number is derived at fill time from the instance keys, so
   * nothing stores it and nothing can disagree with it.
   */
  countPdfMappings?: PdfFieldMapping[] | null;
  /**
   * Repeatable only: this section's INSTANCE COUNT feeds
   * `appInfo.jointApplication` at save time — a count ABOVE
   * `jointApplicationThreshold` reports the application as joint.
   *
   * The structural counterpart to the field-level `jointApplicationSelector`.
   * A form with a repeatable "Account holder" section already says whether the
   * account is joint by how many holders the advisor added; a separate "Is this
   * joint?" dropdown is a second source of truth the advisor can contradict with
   * one click on Add Another.
   *
   * Both tags feed the same slot and the same last-answered-wins waterfall, so a
   * tagged section and a tagged field may coexist and the later one in render
   * order wins. They cannot collide inside one section — a field carrying the tag
   * cannot be placed in a repeatable section.
   *
   * Requires `repeatable`; the backend refuses the pairing and drops the flag
   * when repeatability is switched off. Absent / false = untagged.
   */
  jointApplicationSelector?: boolean | null;
  /**
   * The instance count `jointApplicationSelector` must EXCEED to read as joint.
   *
   * Two shapes of repeatable section say "joint", and only the originator knows
   * which one they authored. A per-party section that always holds the primary
   * party (RDSP's Accountholder Details, min 1 / max 2) is joint on its SECOND
   * instance — threshold 1, the default and the only reading this tag had before
   * the number was authorable. An optional block holding ONLY the extra party
   * (RESP's joint subscriber, min 0 / max 1) is joint as soon as it holds
   * anything — threshold 0, a shape the old fixed "more than one" could never
   * express: capped at one instance, it could never be exceeded.
   *
   * Absent = 1. A threshold at or above `maxInstances` is refused — it could
   * never be exceeded, so the tag would be permanently "not joint".
   */
  jointApplicationThreshold?: number | null;
};

export type FormRow = {
  id: string;
  columns: FormColumn[];
};

export type FormColumn = {
  id: string;
  /** Null = empty slot (the builder shows it as a drop target). */
  field: WorkflowField | null;
};

export type WorkflowFieldType =
  | 'ShortText'
  | 'LongText'
  | 'Number'
  | 'SingleSelect'
  | 'RadioGroup'
  | 'CheckboxGroup'
  // Boolean inputs — identical authoring shape, different runtime control.
  | 'Toggle'
  | 'Checkbox'
  // Layout blocks — non-input. The field's Label is the rendered content for
  // StaticText / Paragraph; Divider's Label is a canvas-only identifier.
  | 'StaticText'
  | 'Paragraph'
  | 'Divider'
  // Date input — runtime renders a calendar picker; authoring carries only the
  // standard Label + Required + Hint surface.
  | 'Date'
  // Layout block — renders Options as <ul>/<ol> at runtime. ListStyle property
  // chooses bullets vs. numbers.
  | 'BulletedList'
  // Layout block — tinted callout. Renders Label inside a colored box with
  // an optional leading icon. backgroundColor + icon are Notice-only.
  | 'Notice'
  // Repeatable rows — the originator authors a single row template (N column
  // specs); the advisor adds 1..N instances of that row at runtime via
  // "+ Add Another Row". Value persisted as an array of objects keyed by
  // each column's stable id.
  | 'RepeatableRow'
  // Layout block — static image with an optional bilingual caption. The image
  // is uploaded via the help-images endpoint (general-purpose for workflow-
  // scoped image uploads); the field stores only the URL.
  | 'Image';

/**
 * One column inside a {@link WorkflowField}'s repeatable row template.
 * Stable `id` keys each per-row value entry; the column's `type` picks
 * which scalar renderer + validator runs inside the row.
 */
/**
 * Authored wording for one runtime error message. `key` names the rule that raised it — the
 * i18n key under `formRenderer.validation` without the plural suffix ('required', 'sin.checksum',
 * 'date.noPast', 'length.exact', 'uniqueness', 'sum', …). `fr` blank falls back to `en`.
 * `{{count}}`-style placeholders are filled from the rule's params like the generic copy.
 */
export type ValidationMessage = { key: string; en: string; fr?: string | null };

export type RepeatableColumn = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr: string;
  required: boolean;
  placeholder?: string | null;
  maxLength?: number | null;
  /** Fewest characters a FILLED cell may hold (the RDSP "Fund number" is exactly 5: min = max).
   *  Text columns only; null = no minimum. A blank optional cell is never held to it. */
  minLength?: number | null;
  /** Smallest / largest figure a FILLED Number cell may hold (a fund's minimum initial
   *  investment, a cap on a one-time amount). Number columns only; null = unbounded.
   *
   *  Deliberately NOT fed to the input as an AntD `min`/`max`, which would silently rewrite
   *  the advisor's figure to the bound on blur: an out-of-range cell is NAMED and holds Next,
   *  the way every other column rule behaves. A blank optional cell is never held to them. */
  min?: number | null;
  max?: number | null;
  options: SelectOption[];
  validate?: string | null;
  /** Companion to `validate: 'regex'` (text columns): the cell must match it. Null otherwise. */
  regexPattern?: string | null;
  /** Authored bilingual message for a cell that fails `regexPattern`; the renderer falls back
   *  to a generic one. */
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  /** Date columns only: a day before today is refused (and greyed in the picker). */
  noPastDates?: boolean | null;
  /** Per-rule error wording (see {@link ValidationMessage}). Empty / absent = generic messages. */
  validationMessages?: ValidationMessage[] | null;
  inputRestriction?: string | null;
  /** LEGACY single AcroForm handle (may carry a `{N}` token). Read-only; the
   *  mapping tool now writes per-row-index handles into {@link mappings}. */
  internalName?: string | null;
  /** Per-row-index AcroForm handles (1-based `rowIndex`), authored by the visual
   *  mapping tool. Each repeatable row maps independently. */
  mappings?: RepeatableColumnMapping[] | null;
  /** Per-column width as a percentage of the row (1–100). Columns share the row
   *  by these percentages; null/undefined = equal share. Values need not sum to
   *  100 — the runtime grid normalizes them proportionally. */
  widthPercent?: number | null;
  /** Bilingual help description shown as a tooltip beside the column name in the
   *  builder and at runtime. Null/blank = no help bubble. */
  helpEn?: string | null;
  helpFr?: string | null;
  /** Dual-unit money column — the advisor flips the WHOLE column between a
   *  dollar amount and a percentage from a control in the column header.
   *  Number columns only; null/undefined on every ordinary column. */
  unitToggle?: ColumnUnitToggle | null;
  /** How a Number column's cells are presented: `'currency'` ($ prefix + thousands
   *  separator) or `'percentage'` (% suffix). Null/undefined = a plain figure.
   *
   *  The scalar field's `numberFormat`, read by the same renderer. For a column whose
   *  unit never changes — the counterpart to {@link unitToggle}, which lets the ADVISOR
   *  choose. The two are mutually exclusive and the BE refuses the pair. */
  numberFormat?: string | null;
};

/**
 * Configuration for a dual-unit money column.
 *
 * The per-mode totals live here rather than on the field's `sumValidation`
 * because they are part of what the unit MEANS: RDSP needs dollars to total the
 * Contribution Amount while percentages total 100, and RIF needs percentages to
 * total 100 on two separate columns of one table. Either mode may carry NO
 * target — RIF leaves dollar mode unconstrained.
 */
export type ColumnUnitToggle = {
  /** Which unit the column starts in. */
  defaultMode: 'dollar' | 'percent';
  /** Header control label; falls back to "$/%" when absent. */
  labelEn?: string | null;
  labelFr?: string | null;
  dollarTargetFieldId?: string | null;
  dollarTargetConstant?: number | null;
  percentTargetFieldId?: string | null;
  percentTargetConstant?: number | null;
  /** The dollar target applies only while ALL of these hold; null/empty = always.
   *  Non-Reg: funds total the cheque amount only when no transfer is also selected. */
  dollarTargetConditions?: FileEntryCondition[] | null;
};

/** One AcroForm handle for a single (1-based) repeatable row of a column. */
export type RepeatableColumnMapping = {
  rowIndex: number;
  internalName: string;
  /** Null/absent on an ordinary column, where one row maps to one slot. On a
   *  CHOICE column each option carries its own handle — the PDF renders the
   *  choice as checkbox widgets, so the handle is a `field:exportValue` key
   *  (e.g. `Check Box4.01c:W`) rather than a text slot. */
  optionValue?: string | null;
  /** Which PART of the cell's value this box receives — `year` / `month` / `day`
   *  or a 1-based inclusive range like `4-6`. Null/absent writes the whole value.
   *  The column counterpart of {@link PdfFieldMapping.segment}: a repeatable date
   *  column feeding separate year / month / day comb boxes needs one handle per
   *  part, and without this the whole `2026-11-15` reaches a 4-character box and
   *  is dropped. Refused together with `optionValue` — an option handle ticks a
   *  checkbox and has no value to slice. */
  segment?: string | null;
  /** Separator with which this handle APPENDS to its box instead of replacing
   *  it. `segment`'s mirror: that spreads one value across several boxes, this
   *  gathers several values into one. The TFSA reinvestment row has a single
   *  box for a fund name and a fund number and must read
   *  `BMO Money Market Fund / 1234`; without this the second column simply
   *  overwrites the first. Appends in the template's column order, and only
   *  when the box already holds something, so a blank earlier column doesn't
   *  leave a leading separator. Refused together with `optionValue` for the
   *  same reason `segment` is. Null/absent replaces, as it always has. */
  joinWith?: string | null;
  /** Where this handle's value comes from. Null/absent — every mapping written
   *  before this existed — is the CELL at `rowIndex`, unchanged.
   *
   *  `total` writes the column's cross-row SUM: the RIF fund table prints a
   *  total under its Amount and Annual Payment columns, which the legacy
   *  generator computed in code and `sumValidation` only ever validated.
   *
   *  `unit` ticks the "$ or %" checkbox for a dual-unit column, with
   *  `optionValue` naming the mode (`dollar` / `percent`).
   *
   *  `rowIndex` is meaningless for both — a total and a mode belong to the
   *  whole column — but stays set so the mapping identity needs no special
   *  case. */
  source?: 'total' | 'unit' | null;
  /** Restricts this handle to ONE language of the entry's document. Null/absent
   *  applies to both, which is what every existing column handle does.
   *
   *  For templates that share a box NAME but put it on a different ROW. The
   *  RDSP PAC start-date table is the case: `year1j` is row 4's year box in the
   *  English file and row 3's in the French one, so no unscoped handle can be
   *  right in both — one language always prints a year on the wrong line. */
  language?: 'en' | 'fr' | null;
  /** Restricts this handle to ONE mode of a dual-unit column. Null/absent applies
   *  in both, which is what every handle authored before this existed does.
   *
   *  `language`'s mirror, for templates that give the two units their own BOX
   *  rather than one box plus a "$ or %" tick. Both transfer authorizations do:
   *  2013 prints `Text47` under `%` and `Text48` under `$`, 2012 `%Amount1` and
   *  `$Amount1`. An unscoped handle cannot be right in both modes — it prints a
   *  percentage into the dollar column and leaves the percent box blank — and a
   *  mapping cannot carry conditions, so the scope belongs on the handle.
   *
   *  Meaningless on a `source` handle: a total and a unit tick belong to the
   *  whole column, and the unit tick already names its mode in `optionValue`. */
  unitMode?: 'dollar' | 'percent' | null;
  /** WHICH document this handle fills — the column counterpart of
   *  {@link PdfFieldMapping.pdfEntryId}, and read with the same `EMPTY_GUID`
   *  "applies to every document" sentinel. Null/absent means the same thing, and
   *  is what every handle authored before columns carried an entry id reads as.
   *
   *  Its absence was a bug, not a simplification: column handles were keyed on
   *  `(rowIndex, optionValue, segment)` alone, so two file entries holding the
   *  SAME document shared one set of them — mapping a fund-name column on one
   *  PDF filled, and showed as mapped on, the other. Two entries are two
   *  documents even when uploaded from one file. */
  pdfEntryId?: string | null;
};

/**
 * Reference to a prior-step field whose current value seeds another field
 * on step entry. Both ids are required; the BE refuses self-reference and
 * empty ids. See `WorkflowField.sourceField`.
 */
export type SourceFieldRef = {
  stepId: string;
  fieldId: string;
  /**
   * Conditional prefill (RDSP QA D-15 / D-22). Same shape and evaluator as field
   * conditions. When set, a source in a repeatable section is tried instance by
   * instance (0, 1, …) with the values scoped to that instance — a condition on a
   * section that repeats with the source reads the same instance — and the FIRST
   * instance that passes is copied. None passing means nothing is copied. Absent /
   * empty = unconditional, from instance 0 (the behaviour before this existed).
   */
  conditions?: FileEntryCondition[] | null;
  conditionCombinator?: 'all' | 'any' | null;
  /** 0-based instance of the TARGET's repeatable section to write ("Caregiver 1
   *  only"). Null / absent = every instance. Ignored on a non-repeatable target. */
  targetInstance?: number | null;
};

/**
 * Cross-row sum rule for a {@link WorkflowFieldType.RepeatableRow} field. See
 * `WorkflowField.sumValidation`. At most one of `targetFieldId` /
 * `targetConstant` is set — the domain refuses BOTH, but accepts NEITHER.
 *
 * Neither is the rule that sums without checking: the total still feeds
 * {@link RepeatableSumValidation.amountTag} and a `source: 'total'` PDF handle
 * and still draws the live figure under the table, but nothing blocks Next.
 * `resolveSumTarget` returns null for it, which `isSumSatisfied` has always
 * read as "no check" — the only thing that changed on 2026-10-27 is that an
 * originator can now author it instead of being forced to invent a target.
 */
export type RepeatableSumValidation = {
  /** Column id (matches a `RepeatableColumn.id`) whose values are summed. */
  columnKey: string;
  /** Field id whose current value is the target sum. Null when using a constant
   *  or when the rule carries no target at all. */
  targetFieldId?: string | null;
  /** Absolute target sum. Null when using a target field or no target at all. */
  targetConstant?: number | null;
  /** Optional bilingual caption shown next to the live total in place of the
   *  generic "Current sum" wording (e.g. "Total contributions"). */
  labelEn?: string | null;
  labelFr?: string | null;
  /** Feeds this field's row-sum into appInfo.USDAmount / appInfo.CADAmount at save time.
   *  Null = not tagged (the sum stays authoring/display-only). Independent of the
   *  target — a targetless rule tags its total exactly the same way. */
  amountTag?: 'USDAmount' | 'CADAmount' | null;
};

/**
 * Layout-block types: non-input elements that render their own content (or
 * nothing, in Divider's case) instead of capturing data. Single source of
 * truth so the builder panel + runtime step-completion calc stay in sync —
 * adding a new layout-block type touches only this list.
 */
export const LAYOUT_BLOCK_TYPES: ReadonlySet<WorkflowFieldType> = new Set([
  'StaticText',
  'Paragraph',
  'Divider',
  'BulletedList',
  'Notice',
  'Image',
]);
export const isLayoutBlockType = (type: WorkflowFieldType): boolean => LAYOUT_BLOCK_TYPES.has(type);

/**
 * Baseline vertical breathing room (CSS px) the form renderer applies to
 * StaticText / Paragraph blocks when the originator hasn't set an explicit
 * marginTop / marginBottom. Mirrored in the right-panel slider initial value
 * so what the originator sees matches what runtime renders.
 */
export const DEFAULT_TEXT_MARGIN_PX = 16;

export type SelectOption = {
  value: string;
  /**
   * FILL-TIME French override for {@link value}. When the FRENCH file of a PDF
   * entry is being filled and this option is the answer, mappings write this
   * instead of `value`. Null/empty writes `value`, which is what every option
   * authored before this does.
   *
   * NOT a second stored value, and the distinction is the whole point. The
   * answer the platform persists — the one every condition compares, every
   * appInfo tag reads, and the review recap shows — stays `value` in both
   * languages. A stored value that changed with the UI language would make
   * `equals "yes"` stop matching the moment the advisor switched to French.
   * Only `collectFilledFields` reads this, and only because it already knows
   * WHICH of the entry's two files it is filling.
   *
   * The sub-agreements are the case it exists for: their bulk question's Yes
   * prints "Please refer to attached Bulk Trading Spreadsheet" into the
   * subscriber boxes, and the French agreement must print the French sentence
   * from the same answer. Before this, that took a second read-only radio
   * carrying the text in its LABEL, because `valueSource: "label"` was the only
   * bilingual constant the mapper had.
   */
  valueFr?: string | null;
  labelEn: string;
  labelFr: string;
  // Optional per-item help bubble. Paired EN+FR — BE refuses an unpaired set.
  // Primarily surfaced on BulletedList items today (renders a "?" icon
  // inside the bullet); available on every options-type for future use.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // CheckboxGroup-only — list of OTHER option values on the same group this
  // option CONFLICTS with. At runtime a conflicting option is rendered
  // DISABLED while this one is checked (and vice versa — the renderer reads
  // the declaration both ways), so nothing is ever silently unchecked. The BE
  // refuses this list on any other field type (RadioGroup / SingleSelect are
  // single-pick by construction; BulletedList isn't an input). Null/empty
  // means "no mutual-exclusion rule".
  //
  // The name is historical: this used to clear the listed options instead of
  // disabling them. Kept as-is because it is persisted in DDB and in every
  // seed snapshot.
  clearsOnSelect?: string[] | null;
  // RadioGroup / CheckboxGroup only — id of another field on the SAME step
  // that this option borrows for inline display when selected. Null = no
  // borrow. The runtime hides the borrowed field from normal section flow and
  // renders it inline beneath the option label. The BE refuses two options
  // borrowing the same id within one group, and refuses the property outright
  // on SingleSelect (a dropdown) and BulletedList (not an input).
  revealFieldId?: string | null;
  // `revealFieldId`'s plural: the option borrows a whole SECTION on the same
  // step, and the runtime replays that section's title, rows and columns inside
  // the option's card. For follow-ups that are a BLOCK rather than one input —
  // the BMO transfer delta reveals eight fields under "Incoming transfer".
  //
  // A section rather than a list of ids because two things then come free: the
  // authored row grouping (paired half-width fields stay side by side) and
  // address-role grouping, which `indexStepFields` scopes PER SECTION — so the
  // borrowed block gets its own Canada Post sibling-fill scope and cannot
  // cross-fill another address on the step.
  //
  // Same rules as `revealFieldId`: RadioGroup / CheckboxGroup only, at most one
  // option per section. The BE refuses both properties on one option — there is
  // no authored order between a field and a section in the same slot.
  revealSectionId?: string | null;
  // Per-option AcroForm checkbox mappings — Radio / SingleSelect / CheckboxGroup
  // only. When the advisor picks this option (or leaves it ticked for a
  // CheckboxGroup), the runtime CHECKs each mapped box at fill time. A list so
  // one option maps to many checkbox widgets across many PDFs (1-to-many).
  // Complements the field-level `pdfMappings` (text-slot fills). BE refuses on
  // other types.
  pdfMappings?: PdfFieldMapping[] | null;
  // Registry KEY (not a path) for the icon shown on this option's card —
  // resolved through `form-renderer/optionIcons.ts`. Only rendered when the
  // owning field sets `displayAs: 'card'`; a list-rendered group ignores it.
  // BE refuses on field types that can't lay options out as cards
  // (RadioGroup / CheckboxGroup are the only two). Null = no icon.
  icon?: string | null;
  // RadioGroup only — "this answer belongs on a different form". Picking the
  // option pops a modal in preview + advisor runtime offering to take the user
  // to the destination; cancelling clears the answer, because the answer isn't
  // one this form can carry. Exactly one of the two destinations is ever set:
  //   - `redirectTemplateId` — another workflow on this platform. The runtime
  //     routes by MODE (preview → that workflow's preview, advisor → a fresh
  //     application on it), so the same authored value works on both surfaces.
  //   - `redirectUrl` — an absolute http(s) destination outside the platform.
  // Both null = no redirect, which is every option authored before this.
  // RadioGroup-only on the BE too: a single-pick control is the only one where
  // "you answered your way off this form" is unambiguous — a CheckboxGroup can
  // hold the redirect answer alongside three others.
  redirectTemplateId?: string | null;
  redirectUrl?: string | null;
  // Bilingual modal body explaining WHY the destination changed (FR falls back
  // to EN, as everywhere else). Optional — the runtime falls back to generic
  // copy. Deliberately authored rather than derived from the target workflow's
  // name: the name would have to be denormalized at authoring time (and go
  // stale when the target is renamed) or fetched at runtime by an advisor
  // session that has no read on another workflow.
  redirectMessageEn?: string | null;
  redirectMessageFr?: string | null;
  // RadioGroup / SingleSelect only — "Selected by default". The runtime fills
  // this option in while the field is empty AND its step has not yet been
  // shown to the advisor; it never overwrites an answer, never re-fills a
  // value the advisor cleared, and never writes over a resumed application's
  // saved answers. At most one per field (the BE refuses two). A default is
  // an ordinary answer once filled — it reaches review, snapshot and PDFs.
  isDefault?: boolean | null;
  // RadioGroup / SingleSelect / CheckboxGroup — which CLIENT languages this
  // option is offered in. Null / absent = both. Keyed off the application's
  // PDF language (the client's, chosen on the Welcome step), NOT the advisor's
  // UI language: the use case is a paper form with no box for the option (the
  // French title list has no "Ms."). A hidden option's answer is cleared when
  // the language changes under it.
  languages?: ('en' | 'fr')[] | null;
};

/**
 * One configurable input field on a DynamicForm step. Property keys are all-nullable
 * so the same shape carries every {@link WorkflowFieldType} — the BE rejects keys
 * that don't apply to the field's type at the domain layer.
 */

/**
 * One field-level PDF AcroForm mapping: the uploaded PDF entry it targets
 * (`pdfEntryId` = a PdfFileUpload step's fileEntry id) plus the AcroForm field
 * name. A field carries a list so it can populate many AcroForm fields across
 * many PDFs (1-to-many), including more than one field within the same PDF.
 * `pdfEntryId === EMPTY_GUID` is the legacy "applies to every document" sentinel
 * for mappings that predate per-entry scoping.
 */
export type PdfFieldMapping = {
  pdfEntryId: string;
  acroFieldName: string;
  /** 1-based instance of a repeatable section this mapping targets; null/absent
   *  for a non-repeatable field. */
  instanceIndex?: number | null;
  /** Which PART of the field's value this mapping writes — `year` / `month` /
   *  `day`, or a 1-based inclusive character range like `4-6`. Null/absent
   *  writes the whole value, which is what every pre-existing mapping does.
   *  Applied by `form-renderer/pdfSegment.ts`. */
  segment?: string | null;
  /** Restricts this mapping to ONE language of the entry's document — `en` or
   *  `fr`. Null/absent applies to both, which is what every pre-existing
   *  mapping does.
   *
   *  For the templates whose AcroForm names agree but whose meanings do not.
   *  RIF's language checkbox is `Check Box4` in both files with the same two
   *  export values, but the boxes are SWAPPED: `Yes` sits beside "English" in
   *  the English form and beside "Français" in the French one. Both names
   *  resolve in both templates, so — unlike the TFSA beneficiary rows, which
   *  differ in case and therefore miss harmlessly — nothing would stop the
   *  wrong handle from overwriting the right one. */
  language?: 'en' | 'fr' | null;
  /** APPENDS this field's value to whatever is already in the box, separated by
   *  this string, instead of overwriting. The mirror of `segment`: where segment
   *  splits one value across boxes, `joinWith` merges several fields into one.
   *
   *  For a paper form that gives ONE line to what the smart form collects as
   *  several inputs — the RDSP transfer form's single relinquishing-address line
   *  takes street + city + province. Without it the last field written simply
   *  wins and the box prints just the province.
   *
   *  Only appends when the box already holds something, so a blank earlier field
   *  leaves no stray leading separator. Null/absent overwrites, which is what
   *  every pre-existing mapping does. */
  joinWith?: string | null;
  /** WHICH RENDERING of the answer this box receives. Null/absent writes the
   *  value the platform STORES, which is what every pre-existing mapping does.
   *
   *  - `label` — the chosen option's label in the language of the document being
   *    filled, instead of its stored value. The sub-agreements' province picker
   *    stores `YT` where the paper form's line reads "Yukon".
   *  - `formatted` — the number run through the field's own `numberFormat`
   *    (currency / percentage) instead of bare digits. A subscription amount
   *    printed `250000` on a document the client signs.
   *  - `number` — the same, but with NO currency symbol or percent sign:
   *    `250,000.00`. For the very common paper-form shape where the symbol is
   *    already printed on the page and the box holds only the figure — which is
   *    every amount box on the five sub-agreements, where `formatted` lands as
   *    `$ $250,000.00`.
   *
   *  Opt-in per mapping, deliberately: `$250,000.00` is eleven characters where
   *  `250000` is six, and a COMB box handed more characters than it has cells
   *  prints BLANK rather than truncating. Formatting every mapped number would
   *  silently empty the combed amount boxes on Non-Reg / TFSA / RIF. */
  valueSource?: PdfMappingValueSource | null;
  /** Holds this mapping OUT of the fill-time waterfall: it is skipped while the
   *  shared box is being overwritten and applied after the whole walk, appending
   *  to whatever won. False/absent takes part in the waterfall, which is what
   *  every pre-existing mapping does.
   *
   *  Not a synonym for `joinWith`, which is about the SEPARATOR and still writes
   *  at the mapping's own point in the walk — so a plain `joinWith` mapping is
   *  wiped by any ordinary mapping that happens to sit later in the form, and
   *  getting one box right meant reordering the form. An addendum cannot be
   *  wiped, because nothing in the waterfall runs after it. The dealer box that
   *  has to print "<dealer name> <dealer code>" from two fields is the case it
   *  exists for.
   *
   *  The two COMPOSE: `isAddendum` decides WHEN the value is written, `joinWith`
   *  the string it is separated by (a single space when `joinWith` is unset).
   *  Text only — a checkbox is ticked, not appended to. */
  isAddendum?: boolean | null;
  /** Fires this mapping ONLY while the field's answer is this option value.
   *  Null/absent fires on whatever the answer is, which is what every
   *  pre-existing mapping does.
   *
   *  The gate a FIELD-level mapping never had. An option-level mapping is
   *  implicitly scoped to its own option — it ticks a box when that option is
   *  picked — but a field-level one writes whatever is stored. So a yes/no
   *  question whose Yes prints a sentence prints the literal `"no"` into the
   *  same box on the No branch. That the later fields in the walk usually
   *  overwrite it is a property of one form's field order, not a rule, and it
   *  is not a property anyone re-derives before editing that form.
   *
   *  Distinct from `language`, which scopes a mapping to one FILE of the entry.
   *  The two COMPOSE, and the sub-agreements use both: the bulk sentence is
   *  written only on Yes (this), in the language of the file being filled
   *  (`SelectOption.valueFr`). */
  optionValue?: string | null;
};

/** Legal values for `PdfFieldMapping.valueSource`. Mirrors the BE's
 *  `PdfMappingValueSource`; the two are covered by matching tests. */
export type PdfMappingValueSource = 'label' | 'formatted' | 'number';

/** The all-zero GUID the BE uses as the "applies to every document" sentinel
 *  on a legacy PDF mapping (rehydrated from the old scalar `internalName`). */
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export type WorkflowField = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr: string;
  required: boolean;
  placeholder?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  integerOnly?: boolean | null;
  options: SelectOption[];
  // Help bubble (per spec §6.3). Both halves are set together or both null.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // Hint — short note shown under the input at runtime. EN+FR independent.
  hintEn?: string | null;
  hintFr?: string | null;
  // FontSize (CSS px) + Color (hex) — only meaningful on StaticText / Paragraph.
  fontSize?: number | null;
  color?: string | null;
  // MarginTop + MarginBottom (CSS px). Renderer defaults to 20 px when unset.
  marginTop?: number | null;
  marginBottom?: number | null;
  // ListStyle — only meaningful on BulletedList. 'disc' (default) or 'decimal'.
  listStyle?: string | null;
  // FontWeight — StaticText / Paragraph only. 'thin' | 'regular' | 'bold'.
  fontWeight?: string | null;
  // InputRestriction — ShortText / LongText only.
  // 'digits' | 'letters' | 'alphanumeric'. Runtime strips non-matching chars.
  inputRestriction?: string | null;
  // BackgroundColor + icon — Notice only. Renderer defaults to BMO blue +
  // info icon when unset on a Notice.
  backgroundColor?: string | null;
  icon?: string | null;
  // DisplayAs — Notice only. 'inline' (default) renders the callout in
  // document flow; 'modal' pops a modal dialog when the field's visibility
  // condition flips true.
  //
  // ALSO used by RadioGroup / CheckboxGroup with a separate, disjoint
  // vocabulary: 'list' (default) is the standard control-then-label stack,
  // 'card' renders each option as a bordered tile with an optional
  // per-option icon. The BE validates the two vocabularies independently, so
  // a Notice can't be 'card' and an option group can't be 'modal'.
  displayAs?: 'inline' | 'modal' | 'list' | 'card' | string | null;
  // BlockingValues — RadioGroup / SingleSelect / CheckboxGroup. When the
  // advisor's value for this field matches these option values, FormShell
  // disables Next on the step that owns this field. Lets the originator mark
  // an eligibility gate without copy-pasting visibility conditions onto every
  // later step.
  blockingValues?: string[] | null;
  // BlockingValueOperator — CheckboxGroup only. 'or' (default) blocks when the
  // selection contains any listed value; 'and' blocks only when it contains
  // all of them. RadioGroup / SingleSelect are always OR.
  blockingValueOperator?: 'and' | 'or' | null;
  // BlockContinueAlert — when true (and blockingValues is set), the renderer
  // pops an alert modal each time the advisor selects a blocking answer.
  blockContinueAlert?: boolean | null;
  /** With blockContinueAlert: closing the alert clears the step and returns to the Welcome step. */
  blockContinueRestart?: boolean | null;
  /** Choice fields: answers this field hides are cleared (opt-in; QA TFSA D05/D15/D16). */
  clearHiddenAnswers?: boolean | null;
  /**
   * Choice fields: the step whose answers are ALL dropped — and whose prefills and defaults
   * re-run — whenever this field's answer changes (opt-in; RDSP "Who is filling out this
   * form" vs Beneficiary Details). Reaches what `clearHiddenAnswers` cannot: fields this
   * field only locks or unlocks (`disableConditions`) rather than hides. See
   * `resetStepAnswers.ts`.
   */
  resetStepId?: string | null;
  /**
   * @deprecated Inert. Used to make the "none of these" option clear its siblings instead of being
   * locked while they were ticked (QA TFSA TF-08 AC2). Option conflicts now always DISABLE rather
   * than clear, so nothing reads this. Still accepted and persisted by the BE, and still present on
   * older templates; unreachable from the builder.
   */
  exclusiveOptionClears?: boolean | null;
  /** RepeatableRow: a blank row the advisor added is flagged at Next instead of ignored (opt-in; QA TFSA D08). */
  checkBlankRows?: boolean | null;
  // BlockContinueMessage — bilingual explanation for the blocking answer.
  // Shown as error text under the field, as the alert-modal body, and as
  // the disabled-Next tooltip (FR falls back to EN).
  blockContinueMessageEn?: string | null;
  blockContinueMessageFr?: string | null;
  // HideLabel — when true, FE renderer suppresses the field label above
  // the input. The label is still stored (BE requires it for identification),
  // it just doesn't render.
  hideLabel?: boolean | null;
  // ShowInReview — StaticText / Paragraph / BulletedList only. The Application
  // Review recap strips every layout block by default (they're authored
  // guidance, not answers); this opts one block back in so its copy recaps
  // alongside the answers. Refused on every other type at the BE.
  showInReview?: boolean | null;
  /** ReviewAsCheckbox — Checkbox only. The Application Review recap renders the
   *  tick-box as a disabled checkbox beside its label — ticked or not — instead
   *  of the "label: ✓" answer row every other input recaps as. An eligibility
   *  gate reads as an agreement, not a value. Refused on every other type at
   *  the BE. */
  reviewAsCheckbox?: boolean | null;
  // VerticalOptions — RadioGroup / CheckboxGroup only. When true the renderer
  // stacks the options vertically (one per line) instead of the default
  // horizontal flow.
  verticalOptions?: boolean | null;
  /** AdvisorRepCodeSelector — SingleSelect only. The renderer populates the
   *  dropdown from the signed-in advisor's `representativeCodes` instead of
   *  `options` (which the domain refuses to author on such a field), and the
   *  answer becomes `appInfo.repCode` on the save call. Forced required and
   *  non-read-only; refused inside a repeatable section. */
  advisorRepCodeSelector?: boolean | null;
  /** FundCodeSelector — SingleSelect only. A PASSIVE tag: the dropdown is
   *  authored as usual and the picked option's `value` becomes
   *  `appInfo.fundCode` on the save call. Forces nothing about required /
   *  readOnly / options; refused alongside `advisorRepCodeSelector` on the same
   *  field, and inside a repeatable section. */
  fundCodeSelector?: boolean | null;
  /** AccountSubTypeSelector — SingleSelect / RadioGroup. `fundCodeSelector` aimed
   *  at a different slot: the field is authored as usual and the picked option's
   *  `value` becomes `appInfo.accountSubType` (BMO's reg/non-reg account
   *  sub-type) on the save call. Forces nothing about required / readOnly /
   *  options; refused alongside either other appInfo tag on the same field, and
   *  inside a repeatable section. Wider than `fundCodeSelector` by one type —
   *  a sub-type list is short enough to author as radios. */
  accountSubTypeSelector?: boolean | null;
  /** JointApplicationSelector — SingleSelect / RadioGroup / Toggle / Checkbox /
   *  CheckboxGroup. The same passive tag aimed at `appInfo.jointApplication`,
   *  which is a BOOLEAN rather than a code: the field is authored as usual and
   *  its answer is COERCED to true/false at save time (`yes` / `y` / `true` /
   *  `1` / `oui` / `vrai`, or any value containing `joint`; everything else is
   *  false). Hence the widest allow-list of the four appInfo tags — a
   *  Toggle/Checkbox yields a boolean outright, and a CheckboxGroup's list
   *  collapses with ANY. Forces nothing about required / readOnly / options;
   *  refused alongside any other appInfo tag on the same field, and inside a
   *  repeatable section. */
  jointApplicationSelector?: boolean | null;
  /** BulkApplicationSelector — the same five types, aimed at `appInfo.bulkApplication`:
   *  "is this one subscription standing in for a list of beneficial subscribers?", which
   *  the sub-agreement forms ask as "Is this a bulk request?" and then answer with a
   *  spreadsheet instead of subscriber fields.
   *
   *  Structurally `jointApplicationSelector`'s twin, with ONE deliberate difference: the
   *  vocabulary. That tag also reads any value containing `joint` as true, because
   *  "compte conjoint" is French for a joint account. This one reads the plain words
   *  only (`yes`/`y`/`true`/`1`/`oui`/`vrai`), so a relationship option spelling
   *  "Conjoint" cannot report a bulk request. Refused alongside any other appInfo tag. */
  bulkApplicationSelector?: boolean | null;
  /** AmountCurrencyFieldId — Number only, requires `amountTag`. The SingleSelect /
   *  RadioGroup whose answer says which currency this amount is in: USD routes it to
   *  `appInfo.USDAmount`, CAD to `appInfo.CADAmount`. While that field is UNANSWERED the
   *  authored `amountTag` stands, which is how "CAD unless they pick USD" is expressed.
   *
   *  Exists because a row holds one field per column: splitting the amount into a CAD twin
   *  and a USD twin routed correctly but could never be laid out, since the hidden twin
   *  keeps its grid cell and strands the Currency radio beside an empty box. */
  amountCurrencyFieldId?: string | null;
  // MaxRows — RepeatableRow only. Advisor-side cap on the number of rows;
  // null = unlimited. The "+ Add Another Row" button hides once reached.
  maxRows?: number | null;
  /** RepeatableRow only. True = the FIRST row can never be removed; the advisor
   *  deletes only rows they added. Independent of `displayAs` — a TABLE can
   *  anchor its first row, which card layout used to imply on its own.
   *  Null/undefined = fall back to the layout default. */
  anchorFirstRow?: boolean | null;
  // Validate — semantic rule the FE checks on blur and surfaces as an inline
  // error. sin/email/phone/postal are ShortText only; 'regex' is allowed on
  // ShortText + LongText.
  validate?: string | null;
  // RegexPattern — companion to validate === 'regex'. regexMessage — optional
  // bilingual override shown when a value fails the pattern.
  regexPattern?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  // AddressAutocomplete — ShortText only. 'canada-post' swaps the plain
  // Input for an AntD AutoComplete backed by Canada Post's AddressComplete
  // API; on selection, the renderer fills sibling fields tagged with the
  // matching `addressRole` in the same section. Null = no autocomplete.
  addressAutocomplete?: 'canada-post' | null;
  // AddressRole — ShortText / SingleSelect only. Tags this field as one of
  // the slots an AddressComplete retrieve fills:
  //   - 'street' (the autocomplete-driving field itself)
  //   - 'city' / 'postal' / 'apt' (typically ShortText)
  //   - 'province' (typically SingleSelect with 2-letter-code values)
  //   - 'full' (ShortText that holds the WHOLE address on one line —
  //     "232-46 Glamis Green SW, Calgary, AB T3E 6V3"). Typically stamped on
  //     the autocomplete field itself when the form wants a single address
  //     line instead of a street / city / province / postal compound.
  //   - 'aptStreet' (ShortText that holds the unit AND the street on one line
  //     — "232-46 Glamis Green SW" — but nothing after it). The middle ground
  //     between 'street' and 'full': the city / province / postal siblings
  //     still get their own slots, only the unit folds into the street line.
  //     Use it when the PDF has a single "Address" line but separate City /
  //     Province / Postal boxes, or alongside an 'apt' slot when the form
  //     needs both the broken-out unit and a combined line.
  // Sibling fill matches by role within the trigger field's section, so
  // renaming a label or running multiple Address compounds on one step
  // doesn't break the cross-field population. Null = not part of a
  // sibling group.
  addressRole?: 'street' | 'city' | 'province' | 'postal' | 'apt' | 'full' | 'aptStreet' | null;
  // MinAge — Date only. Whole years. Runtime disables dates that would make
  // the advisor younger than this; advisor + originator authoring share the
  // same property. Null = no min age.
  minAge?: number | null;
  // NoPastDates — Date only. Runtime disables (and refuses typed) days before
  // today; today itself is allowed. Null / false = off.
  noPastDates?: boolean | null;
  // MustFollowFieldId — Date only. Id of another Date field this value must be
  // strictly AFTER (an end date after its start date). Resolved per repeatable
  // instance the way conditions are; skipped while the referenced date is
  // blank. Null = no ordering rule.
  mustFollowFieldId?: string | null;
  // DisplayMask — ShortText only, e.g. '###-######'. Every '#' is one digit;
  // every other character is a literal the runtime inserts while the advisor
  // types. PRESENTATION ONLY: the stored value stays the raw digits, so a PDF
  // comb sized for the digits never receives the longer formatted string. A
  // value is complete when it holds one digit per '#'. Null = no mask.
  displayMask?: string | null;
  /** Per-rule error wording (see {@link ValidationMessage}). Empty / absent = generic messages. */
  validationMessages?: ValidationMessage[] | null;
  /** Date only: the oldest allowed age in whole years. Null = no upper bound. */
  maxAge?: number | null;
  /** Date only, with `mustFollowFieldId`: the same day is allowed. */
  mustFollowAllowSame?: boolean | null;
  /** RepeatableRow only: start with as many blank rows as this other table has. */
  rowsFollowFieldId?: string | null;
  // NumberFormat — Number only. 'currency' prepends "$" + thousands separators;
  // 'percentage' appends "%". Null = plain numeric input.
  numberFormat?: 'currency' | 'percentage' | string | null;
  // AmountTag — Number only (refused on a percentage-formatted one, and inside a
  // repeatable section). Sends this field's own answer to appInfo.USDAmount /
  // appInfo.CADAmount at save time. Several tagged sources do NOT add up — the last
  // in form reading order wins, and an unanswered one is skipped rather than
  // overwriting with zero. The RepeatableRow equivalent, which sends a column's
  // TOTAL, is `sumValidation.amountTag`. Null = not tagged.
  amountTag?: 'USDAmount' | 'CADAmount' | null;
  // PdfMappings — field-level PDF AcroForm mappings. A field can map to many
  // AcroForm fields across many PDFs (1-to-many). Empty / omitted when unmapped;
  // refused on layout blocks at the BE.
  pdfMappings?: PdfFieldMapping[] | null;
  // RepeatableTemplate — column specs the runtime renders one row at a time
  // for RepeatableRow fields. Null on every other field type; refused at the
  // BE if stamped on something else.
  repeatableTemplate?: RepeatableColumn[] | null;
  // Cross-row sum rule — RepeatableRow only. Runtime sums the specified
  // Number column across every advisor-added row and — when the rule carries
  // a target — requires the total to equal either an absolute constant or the
  // current value of another field on the workflow. A rule with NO target sums
  // without checking. Null = no cross-row sum at all.
  sumValidation?: RepeatableSumValidation | null;
  // Cross-field uniqueness tag — ShortText only. A COMMA-SEPARATED LIST of
  // group names; every field sharing a name is treated as a peer set at
  // runtime, and a duplicate value across peers blocks Next and paints the
  // offender red. Several names put one field in several independent peer
  // sets (RIF: `rifSin,rifSinSuccessor`). Null / empty = "not in a group".
  // Group names are originator-authored strings; convention: `signatoryEmail`,
  // `signatoryPhone`, etc. Parse with `uniquenessGroupsOf` — never split by hand.
  uniquenessGroup?: string | null;
  /** CheckboxGroup only. Advisor must tick at least this many options
   *  before the step completes. Null = plain `required` (any selection). */
  minSelections?: number | null;
  // Cross-step prefill reference. On step entry, if this field is empty
  // AND the advisor hasn't manually edited it, the runtime copies the
  // referenced field's current value into this slot. Advisor typing severs
  // the link. Refused on layout blocks and RepeatableRow at the BE. The
  // properties panel picker filters to PRIOR steps so the originator
  // can't author a same-step or later-step reference.
  sourceField?: SourceFieldRef | null;
  // ImageUrl — Image only. URL of the uploaded binary (lives in S3, served
  // via the help-images endpoint).
  imageUrl?: string | null;
  // ImageWidthSize — Image only. 'small' (33% of column), 'medium' (66%),
  // 'full' (100%). Renderer defaults to 'full' when unset.
  imageWidthSize?: 'small' | 'medium' | 'full' | string | null;
  // ImageAlignment — Image only. 'left' (default) / 'center' / 'right'.
  imageAlignment?: 'left' | 'center' | 'right' | string | null;
  // Caption — Image only. Bilingual caption rendered beneath the image.
  captionEn?: string | null;
  captionFr?: string | null;
  // Alt — Image only. Bilingual alt text on the rendered <img> for a11y.
  altEn?: string | null;
  altFr?: string | null;
  // Optional originator-authored override for the required-error message
  // shown at runtime when this field is `required` and the advisor leaves it
  // empty. Null/empty → renderer falls back to a generic message.
  requiredMessageEn?: string | null;
  requiredMessageFr?: string | null;
  // ReadOnly — when true, FE renderer disables the input and skips the
  // required-empty check for this field. Value is expected from prefill
  // (sourceValue) or a stored default. Layout blocks refuse this at the BE.
  readOnly?: boolean | null;
  // SourceValue — prefill key. 'advisor.*' (text inputs): the advisor session, resolved via
  // useSession() when the step first mounts. 'application.language' (RadioGroup with en / fr
  // options): the language picked on the Welcome page, until the step is shown or edited.
  sourceValue?:
    | 'advisor.fullName'
    | 'advisor.email'
    | 'advisor.phone'
    | 'advisor.dealerCode'
    | 'advisor.repCode'
    | 'application.language'
    | string
    | null;
  // Visibility conditions — gates whether this field renders at all.
  // Same shape as PDF/section conditions; runtime evaluation lands with the
  // advisor route. A field with conditions[] is not required-gated (the
  // step-completion calc treats it as optional until the evaluator lands).
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  // Field-level "Disable if" — the conditional twin of `readOnly` above: when
  // these MATCH the input is disabled and exempt from the required-empty check,
  // exactly as a statically read-only field is. The two compose by OR at
  // FieldRenderer's single disable point. Unlike a failed visibility rule the
  // field stays rendered and its answer still ships. Inverse polarity of
  // `conditions`; evaluate only via `isDisabledByRule`. Empty / absent = never
  // disabled.
  disableConditionCombinator?: 'all' | 'any' | null;
  disableConditions?: FileEntryCondition[];
};

/**
 * Wire shape for adding/editing a field. The BE picks the relevant keys per
 * `type` and rejects the rest; the FE editor should only set keys that apply
 * to the field type the user is configuring.
 */
export type WorkflowFieldPropertiesPayload = {
  placeholder?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  integerOnly?: boolean | null;
  options?: {
    value: string;
    valueFr?: string | null;
    labelEn: string;
    labelFr?: string | null;
    // Per-item help bubble. Paired (both halves or neither) — BE pair check.
    helpTitleEn?: string | null;
    helpTitleFr?: string | null;
    helpBodyEn?: string | null;
    helpBodyFr?: string | null;
    // CheckboxGroup mutual-exclusion targets — sibling option values this one
    // conflicts with. At runtime they are DISABLED while this option is
    // checked, and it is disabled while any of them is. BE refuses this on
    // non-CheckboxGroup types. (Historical name: it used to clear them.)
    clearsOnSelect?: string[] | null;
    // RadioGroup / CheckboxGroup only — id of a sibling field on the same step
    // that this option borrows for inline display. BE refuses on other types
    // and refuses duplicates within one group.
    revealFieldId?: string | null;
    // Its plural — id of a SECTION on the same step whose rows render inside
    // this option's card. BE refuses it alongside revealFieldId on one option.
    revealSectionId?: string | null;
    // Per-option AcroForm checkbox mappings (1-to-many across PDFs). Managed via
    // the option toggle endpoint; carried here so a full field edit round-trips
    // them instead of clearing them.
    pdfMappings?: PdfFieldMapping[] | null;
    // RadioGroup-only redirect: picking this option sends the user to another
    // form. At most ONE destination per option — BE refuses the pair.
    redirectTemplateId?: string | null;
    redirectUrl?: string | null;
    // Bilingual modal body. BE refuses either half without a destination.
    redirectMessageEn?: string | null;
    redirectMessageFr?: string | null;
    // RadioGroup / SingleSelect only; at most one per field (BE).
    isDefault?: boolean | null;
    // Client languages this option is offered in; null = both.
    languages?: ('en' | 'fr')[] | null;
  }[];
  // Help bubble — both halves must be set together. BE refuses unpaired sets.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // Hint — independent EN + FR optional strings.
  hintEn?: string | null;
  hintFr?: string | null;
  // FontSize (CSS px) + Color (hex). BE refuses on every type except
  // StaticText / Paragraph.
  fontSize?: number | null;
  color?: string | null;
  // MarginTop + MarginBottom (CSS px). Same scope.
  marginTop?: number | null;
  marginBottom?: number | null;
  // ListStyle — BulletedList only.
  listStyle?: string | null;
  // FontWeight — StaticText / Paragraph / Notice.
  fontWeight?: string | null;
  // InputRestriction — ShortText / LongText only.
  inputRestriction?: string | null;
  // BackgroundColor + icon — Notice only.
  backgroundColor?: string | null;
  icon?: string | null;
  // DisplayAs — Notice ('inline' default | 'modal') or the card-capable
  // option groups, RadioGroup / CheckboxGroup ('list' default | 'card').
  displayAs?: 'inline' | 'modal' | 'list' | 'card' | string | null;
  // BlockingValues — RadioGroup / SingleSelect / CheckboxGroup.
  blockingValues?: string[] | null;
  // BlockingValueOperator — CheckboxGroup only. 'or' (default) / 'and'.
  blockingValueOperator?: 'and' | 'or' | null;
  // BlockContinueAlert — alert modal on blocking answer. Requires blockingValues.
  blockContinueAlert?: boolean | null;
  /** With blockContinueAlert: closing the alert clears the step and returns to the Welcome step. */
  blockContinueRestart?: boolean | null;
  /** Choice fields: answers this field hides are cleared (opt-in; QA TFSA D05/D15/D16). */
  clearHiddenAnswers?: boolean | null;
  /** Choice fields: this step's answers are all dropped, and its prefills re-run, on every change of this field. */
  resetStepId?: string | null;
  /** @deprecated Inert — option conflicts always disable rather than clear. Accepted for back-compat. */
  exclusiveOptionClears?: boolean | null;
  /** RepeatableRow: a blank row the advisor added is flagged at Next instead of ignored (opt-in; QA TFSA D08). */
  checkBlankRows?: boolean | null;
  // BlockContinueMessage — bilingual blocking explanation. Requires blockingValues.
  blockContinueMessageEn?: string | null;
  blockContinueMessageFr?: string | null;
  // HideLabel — suppress the field label in the renderer.
  hideLabel?: boolean | null;
  // ShowInReview — StaticText / Paragraph / BulletedList only. Opt the block
  // into the Application Review recap.
  showInReview?: boolean | null;
  // ReviewAsCheckbox — Checkbox only. Recap the tick-box as a disabled checkbox
  // rather than a "label: ✓" answer row.
  reviewAsCheckbox?: boolean | null;
  // VerticalOptions — RadioGroup / CheckboxGroup only. Stack options vertically.
  verticalOptions?: boolean | null;
  // AdvisorRepCodeSelector — SingleSelect only. Session-sourced rep-code picker.
  advisorRepCodeSelector?: boolean | null;
  // FundCodeSelector — SingleSelect only. Tags the dropdown's answer as
  // appInfo.fundCode.
  fundCodeSelector?: boolean | null;
  // AccountSubTypeSelector — SingleSelect / RadioGroup. Tags the field's answer
  // as appInfo.accountSubType.
  accountSubTypeSelector?: boolean | null;
  // JointApplicationSelector — SingleSelect / RadioGroup / Toggle / Checkbox /
  // CheckboxGroup. Tags the field's answer, coerced to a boolean, as
  // appInfo.jointApplication.
  jointApplicationSelector?: boolean | null;
  /** BulkApplicationSelector — the same five types, aimed at `appInfo.bulkApplication`:
   *  "is this one subscription standing in for a list of beneficial subscribers?", which
   *  the sub-agreement forms ask as "Is this a bulk request?" and then answer with a
   *  spreadsheet instead of subscriber fields.
   *
   *  Structurally `jointApplicationSelector`'s twin, with ONE deliberate difference: the
   *  vocabulary. That tag also reads any value containing `joint` as true, because
   *  "compte conjoint" is French for a joint account. This one reads the plain words
   *  only (`yes`/`y`/`true`/`1`/`oui`/`vrai`), so a relationship option spelling
   *  "Conjoint" cannot report a bulk request. Refused alongside any other appInfo tag. */
  bulkApplicationSelector?: boolean | null;
  /** AmountCurrencyFieldId — see the read model above. Number only, requires `amountTag`. */
  amountCurrencyFieldId?: string | null;
  // MaxRows — RepeatableRow only.
  maxRows?: number | null;
  /** RepeatableRow only. True = the first row cannot be removed. */
  anchorFirstRow?: boolean | null;
  // Validate — sin/email/phone/postal (ShortText) or 'regex' (ShortText/LongText).
  validate?: string | null;
  // RegexPattern + bilingual regexMessage — companions to validate === 'regex'.
  regexPattern?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  // AddressAutocomplete — ShortText only. 'canada-post' or null.
  addressAutocomplete?: 'canada-post' | null;
  // AddressRole — ShortText / SingleSelect only.
  addressRole?: 'street' | 'city' | 'province' | 'postal' | 'apt' | 'full' | 'aptStreet' | null;
  // MinAge — Date only.
  minAge?: number | null;
  // NoPastDates — Date only. Null / false = off.
  noPastDates?: boolean | null;
  // MustFollowFieldId — Date only. Another Date field this must be after.
  mustFollowFieldId?: string | null;
  // DisplayMask — ShortText only. '#' = one digit; stored value stays digits.
  displayMask?: string | null;
  /** Per-rule error wording (see {@link ValidationMessage}). Empty / absent = generic messages. */
  validationMessages?: ValidationMessage[] | null;
  /** Date only: the oldest allowed age in whole years. Null = no upper bound. */
  maxAge?: number | null;
  /** Date only, with `mustFollowFieldId`: the same day is allowed. */
  mustFollowAllowSame?: boolean | null;
  /** RepeatableRow only: start with as many blank rows as this other table has. */
  rowsFollowFieldId?: string | null;
  // NumberFormat — Number only.
  numberFormat?: 'currency' | 'percentage' | string | null;
  // AmountTag — Number only. Feeds appInfo.USDAmount / appInfo.CADAmount; last
  // tagged source in reading order wins. Null = not tagged.
  amountTag?: 'USDAmount' | 'CADAmount' | null;
  // PdfMappings — field-level PDF AcroForm mappings (1-to-many). Managed mainly
  // via togglePdfMapping; carried here so a full field edit round-trips the
  // mappings instead of clearing them.
  pdfMappings?: PdfFieldMapping[] | null;
  // RepeatableTemplate — column specs for a RepeatableRow field. Refused
  // on every other type at the BE.
  repeatableTemplate?: RepeatableColumnPayload[] | null;
  // Image-only — URL of the uploaded binary + width / alignment presets.
  imageUrl?: string | null;
  imageWidthSize?: 'small' | 'medium' | 'full' | string | null;
  imageAlignment?: 'left' | 'center' | 'right' | string | null;
  // Image-only — bilingual caption + alt text.
  captionEn?: string | null;
  captionFr?: string | null;
  altEn?: string | null;
  altFr?: string | null;
  // Optional bilingual override for the required-error copy.
  requiredMessageEn?: string | null;
  requiredMessageFr?: string | null;
  // ReadOnly — disables input + skips required-empty check.
  readOnly?: boolean | null;
  // SourceValue — advisor-session prefill key. Domain allow-list.
  sourceValue?: string | null;
  // Cross-row sum rule — RepeatableRow only.
  sumValidation?: RepeatableSumValidation | null;
  // Cross-field uniqueness tag — ShortText only.
  uniquenessGroup?: string | null;
  /** CheckboxGroup only. Advisor must tick at least this many options
   *  before the step completes. Null = plain `required` (any selection). */
  minSelections?: number | null;
  // Cross-step prefill reference.
  sourceField?: SourceFieldRef | null;
};

/** Wire shape for one column inside a RepeatableRow's template. Mirrors the
 *  scalar field payload but only carries the keys relevant to the column's
 *  own scalar type. */
export type RepeatableColumnPayload = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr?: string | null;
  required: boolean;
  placeholder?: string | null;
  maxLength?: number | null;
  minLength?: number | null;
  /** Numeric bounds for a Number column; the BE refuses them on every other type. */
  min?: number | null;
  max?: number | null;
  options?: {
    value: string;
    labelEn: string;
    labelFr?: string | null;
    helpTitleEn?: string | null;
    helpTitleFr?: string | null;
    helpBodyEn?: string | null;
    helpBodyFr?: string | null;
  }[];
  validate?: string | null;
  regexPattern?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  noPastDates?: boolean | null;
  /** Per-rule error wording (see {@link ValidationMessage}). Empty / absent = generic messages. */
  validationMessages?: ValidationMessage[] | null;
  inputRestriction?: string | null;
  internalName?: string | null;
  widthPercent?: number | null;
  helpEn?: string | null;
  helpFr?: string | null;
  /** Dual-unit money column — Number columns only; the BE refuses it elsewhere.
   *  Null / absent on every ordinary column.
   *
   *  `buildProperties` has always sent this (and the BE has always accepted it —
   *  `ColumnUnitTogglePayload`); it was missing here only because the payload
   *  reaches its annotated position through `.map()`, which drops excess-property
   *  checking and hid the omission. A caller that builds a column as a FRESH
   *  object literal — the catalog entries in `CatalogTabContent.tsx` — is held to
   *  the declared shape, so the gap cost them their toggle. */
  unitToggle?: ColumnUnitToggle | null;
  /** `'currency'` / `'percentage'` presentation for a Number column — the BE refuses it on
   *  every other type, and refuses it alongside `unitToggle`. Null / absent = a plain figure. */
  numberFormat?: string | null;
};

/**
 * Editor lock state on a workflow. `null` when no live lock is held (or the existing
 * one has aged past the TTL — the BE drops expired locks from this payload). When
 * present, `heldByCaller` is the BE-computed flag for "is this me?" — render the
 * editor normally when true, render read-only with a "Currently being edited by
 * {name}" banner when false.
 *
 * Note: the holder's Entra OID intentionally never crosses the wire — the boolean
 * is computed server-side from the authenticated request, so the client never
 * sees a colleague's identifier. `holderName` is the display snapshot the BE
 * persisted at lock-acquire time.
 */
export type LockState = {
  heldByCaller: boolean;
  holderName?: string | null;
  lockedAt: string;
  expiresAt: string;
};

export type FileEntry = {
  id: string;
  /** English name. Also the §14.1 DOCNAME BMO receives in EVERY language — never localise that. */
  label: string;
  /** Optional French display name / download name (QA D-31). Null / blank = use `label`. */
  labelFr?: string | null;
  enFileKey?: string | null;
  frFileKey?: string | null;
  // Per-language upload timestamps (ISO-8601 from the BE). The originator
  // drawer renders these as "Uploaded {date}" beneath each file row so the
  // approver / originator can see at a glance which version is in play.
  // Null when no file is attached on that side yet.
  enUploadedAt?: string | null;
  frUploadedAt?: string | null;
  // Gating conditions: ordered list + a shared combinator ("all" / "any").
  // Empty list + null combinator → "always include". Per-condition
  // combinators are intentionally not supported.
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  // Optional filename template with `{field:<internalName>}` and
  // `{today:<format>}` tokens the runtime substitutes at Preview /
  // Download time; `{field:a|field:b}` falls back to the first non-blank
  // alternative. Null = fall back to `${label}.pdf`.
  filenameTemplate?: string | null;
  /**
   * French counterpart of {@link filenameTemplate}, same tokens. Null = fall back to the English
   * template.
   *
   * Without any template a French application already names its download from `labelFr`, so an
   * English-only template is what UN-localises a French download — this is the way back.
   * Advisor-facing only: the §14.1 delivery name is minted from `label` whatever either says.
   */
  filenameTemplateFr?: string | null;
  /**
   * Which instance of a repeatable section this entry's `{field:…}` filename tokens read, as a
   * 0-based decimal string. Null = instance 0 (the long-standing behaviour).
   *
   * Set when the document IS one instance's document — RDSP's "Transfer Forms – Accountholder 2".
   * Only an index is meaningful here; the `'any'` / `'all'` quantifiers a
   * {@link FileEntryCondition} accepts have no reading for a name that needs exactly one value.
   *
   * CAUTION: 0-based, so Accountholder 2 is `'1'`. The `instanceIndex` on a `pdfMappings` entry —
   * which scopes the same document's CONTENT to the same holder — is 1-based and spells it `2`.
   */
  instanceScope?: string | null;
};

export type FileEntryCondition = {
  fieldStepId: string;
  fieldId: string;
  operator: string;
  /** Booleans pack the operator name here so the BE always sees a non-null string.
   *  For a RANGE operator this is the LOWER bound — see {@link FileEntryCondition.valueMax}. */
  value: string;
  /**
   * Upper bound of a RANGE operator, exclusive. Only `age_years_between` uses it:
   * `value` is the minimum age in whole years (inclusive), `valueMax` the maximum
   * (exclusive), so "6 up to the day before the 9th birthday" is `6` / `9`.
   * Absent / null on every other operator, and the BE refuses one set alongside
   * them rather than ignoring it.
   *
   * A second bound on the SAME row rather than a second row, because the two
   * bounds have to bind to the same repeatable INSTANCE. Rows are evaluated
   * independently and only their verdicts are combined (see `combine` in
   * `evaluateConditions.ts`), so `age_years_ge 6 (any)` beside
   * `age_years_lt 9 (any)` is satisfied by a family of a 3-year-old and a
   * 12-year-old — each row finds its own beneficiary. One row carrying both
   * bounds is quantified once, which is the whole point.
   */
  valueMax?: string | null;
  /**
   * Quantifier for a source field that lives inside a REPEATABLE section.
   * Absent = ordinary field, resolved by its bare id. `'any'` = satisfied
   * when at least one instance matches; `'all'` = satisfied only when every
   * instance matches (and never by zero instances). A decimal string (`'0'`,
   * `'1'`, …) names ONE instance by its 0-based index.
   *
   * Orthogonal to `operator` on purpose — folding the quantifier into
   * operator names would double the list forever.
   *
   * **Only matters where the values map is NOT already instance-scoped.** A
   * field's or section's own conditions are evaluated against
   * `scopeValuesToInstance` output, and so is a prefill gate (once per
   * candidate source instance, see `resolvePrefillSourceInstance`) — there a
   * bare condition already means "this instance", and a quantifier would
   * wrongly widen it back out. The raw-map callers are the ones that need it:
   * step conditions, signatory role gates, signature placements, PDF entries
   * and additional documents.
   *
   * The index exists because those raw-map callers had no way to say "holder
   * 1 answered yes" — only "some holder" or "every holder". RDSP's combined
   * "Accountholder 1 / Primary caregiver" role is exactly that question, and
   * `fixedInstanceIndex` cannot answer it: `signerRoster` decides visibility
   * against the raw map and pins the instance only afterwards.
   */
  instanceScope?: 'any' | 'all' | `${number}` | null;
  /**
   * This condition's own connector, letting one rule mix AND and OR:
   * `ALL(and-group) && ANY(or-group)`.
   *
   * Absent (the overwhelmingly common case) = inherit the list's
   * `conditionCombinator`, so a rule authored before this existed evaluates
   * exactly as it always did — `'all'` puts every row in the and-group,
   * `'any'` puts every row in the or-group. The combinator is NOT deprecated:
   * it remains the default for rows that don't override it, and is what the
   * editor stamps on newly added rows.
   *
   * Two groups rather than a nestable tree, deliberately. `(A && B) && (C || D)`
   * is the shape real rules need — "these facts must hold, and at least one of
   * these" — and a tree would have to recurse through the domain record, the
   * DTOs, the DynamoDB read/write, this evaluator, the editor modal and the
   * approver diff to buy an expression nothing has asked for.
   */
  connector?: 'and' | 'or' | null;
};

/**
 * One originator-defined document slot on an AdditionalDocuments step. Unlike
 * FileEntry, the originator never uploads a file here — the advisor supplies
 * it at runtime. allowedExtensions scopes which file types the advisor may
 * pick for this slot; doNotParseToPdf opts the slot out of the platform's
 * default non-PDF -> PDF conversion.
 */
export type AdditionalDocumentEntry = {
  id: string;
  /** English slot name. Also the §14.1 DOCNAME the upload is delivered under, in EVERY language,
   *  and what NEW/REPLACE disposition matches on — never localise that. */
  label: string;
  /** Optional French slot name shown in a French UI (QA D-31). Null / blank = use `label`. */
  labelFr?: string | null;
  /**
   * Optional guidance the ORIGINATOR authors, rendered under this slot's title and above its
   * accepted-formats line. At most {@link MAX_DOCUMENT_DESCRIPTION_LENGTH} characters.
   *
   * Rendered as markdown through the project-wide `RichText`, so `[label](https://…)` becomes a
   * real link — which is the whole reason it is not a plain string on screen. Display only: it
   * reaches the advisor and nobody else, and the §14.1 DOCNAME is still `label`.
   *
   * Null / blank / absent = nothing rendered, which is every entry authored before this existed.
   */
  description?: string | null;
  /** French copy of {@link description}. Null / blank = fall back to the English one, the same
   *  contract `labelFr` follows — an untranslated workflow still shows the guidance. */
  descriptionFr?: string | null;
  allowedExtensions: string[];
  isRequired: boolean;
  doNotParseToPdf: boolean;
  /** Per-entry combined file-size cap (megabytes), between {@link
   *  MIN_DOCUMENT_MAX_FILE_SIZE_MB} and {@link MAX_DOCUMENT_MAX_FILE_SIZE_MB}.
   *  Enforced at upload time. */
  maxFileSizeMb: number;
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  /** Same tokens as `FileEntry.filenameTemplate`. Applied to the
   *  advisor's uploaded file when packaged into the Download-all zip;
   *  the original filename is kept when null. */
  filenameTemplate?: string | null;
};

/** Bounds for the free-form max-file-size input the originator drawer surfaces
 *  for every mandatory-doc entry AND for the step-level optional bucket.
 *  Mirrors the BE range check at `AdditionalDocumentEntry.MinMaxFileSizeMb` /
 *  `MaxMaxFileSizeMb`.
 *
 *  The ceiling is the platform's real transport limit, not a product choice:
 *  file bytes stream through the API Lambda (ADR-0028) and
 *  `StorageOptions.MaxUploadBytes` is 4 MiB. It was 25, which let an originator
 *  configure a cap the upload endpoint would then refuse. Keep these two numbers
 *  moving together. */
export const MIN_DOCUMENT_MAX_FILE_SIZE_MB = 1;
export const MAX_DOCUMENT_MAX_FILE_SIZE_MB = 4;

/** Longest per-language description an originator may author on a mandatory document entry.
 *  Mirrors the BE's `AdditionalDocumentEntry.MaxDescriptionLength`, which refuses anything
 *  longer — keep the two numbers moving together. */
export const MAX_DOCUMENT_DESCRIPTION_LENGTH = 500;

/** Bounds for the COMBINED size caps — the sum across a whole section, not one file.
 *
 *  Deliberately NOT {@link MAX_DOCUMENT_MAX_FILE_SIZE_MB}. That ceiling is the Lambda
 *  transport limit for a SINGLE request; a combined cap bounds a sum over several
 *  individually-capped requests and may legitimately exceed it — a 5 MB total made of
 *  two 2.5 MB uploads breaks no transport rule. Mirrors the BE's
 *  `WorkflowStep.MinCombinedMaxSizeMb` / `MaxCombinedMaxSizeMb`. */
export const MIN_COMBINED_MAX_SIZE_MB = 1;
export const MAX_COMBINED_MAX_SIZE_MB = 200;

/** Bounds on the optional bucket's document count. The ceiling matches what one
 *  `deliver` call can carry (`ApplicationEndpoints.MaxDeliveryKeysPerRequest`): a step
 *  configured above it would let an advisor attach documents that could never ship. */
export const MIN_OPTIONAL_DOCUMENT_COUNT = 1;
export const MAX_OPTIONAL_DOCUMENT_COUNT = 50;

/** What the optional bucket allows when the originator has not said otherwise. Not a new
 *  policy — the number the advisor runtime hard-coded before the setting existed. */
export const DEFAULT_MAX_OPTIONAL_DOCUMENTS = 10;

/** The figures the product asked for, offered as the drawer's starting point rather than
 *  applied as defaults. See `mandatoryCombinedMaxSizeMb` for why nothing is retro-capped. */
export const SUGGESTED_MANDATORY_COMBINED_MAX_SIZE_MB = 5;
export const SUGGESTED_OPTIONAL_COMBINED_MAX_SIZE_MB = 1;

export type ReorderDirection = 'Up' | 'Down';

/**
 * How a workflow presents dates at runtime (the picker's display and typed format, its
 * placeholder, typed-date validation, the review recap). Display only — answers are stored ISO
 * `YYYY-MM-DD` either way. Mirrors the server's `WorkflowDateFormats`.
 */
export type WorkflowDateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY';

export const WORKFLOW_DATE_FORMATS: readonly WorkflowDateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY'];

/** Application Review layout. Null / absent = one block per form section (the original);
 *  'steps' = one block per step, an acknowledgement section recapped as its text + ✓ list. */
export type WorkflowReviewLayout = 'steps';

/**
 * One originator-authored key/value pair on a workflow — the platform's answer to an
 * environment variable. A field binds to one by carrying `sourceValue: 'variable.<key>'`.
 *
 * Mirrors the server's `WorkflowVariable`. Keys are matched CASE-INSENSITIVELY, which is also
 * the uniqueness rule the domain enforces at authoring time.
 *
 * Not a secret store: values are readable by anyone who can read the workflow and are written
 * verbatim into saved answers that travel to BMO.
 */
export type WorkflowVariable = { key: string; value: string };

/** The `sourceValue` prefix that binds a field to a workflow variable. */
export const VARIABLE_SOURCE_PREFIX = 'variable.';

/**
 * The variable key a `sourceValue` names, or null when it names none.
 *
 * Everything after the FIRST prefix is the key, so a namespaced key (`fund.default`) survives
 * intact — the remainder is never re-split.
 */
export function variableSourceKeyOf(sourceValue: string | null | undefined): string | null {
  if (!sourceValue?.startsWith(VARIABLE_SOURCE_PREFIX)) return null;
  const key = sourceValue.slice(VARIABLE_SOURCE_PREFIX.length);
  return key.length > 0 ? key : null;
}

/**
 * The value bound to `key`, or null for "no such variable".
 *
 * Null is the "nothing to say" answer and callers must leave the slot alone on it — a binding
 * may legitimately outlive its variable (they are authored through different endpoints), and
 * blanking the field would turn a stale binding into data loss.
 */
export function resolveWorkflowVariable(
  variables: readonly WorkflowVariable[] | null | undefined,
  key: string | null | undefined,
): string | null {
  if (!key) return null;
  const hit = variables?.find((v) => v.key.toLowerCase() === key.toLowerCase());
  return hit ? hit.value : null;
}

export type WorkflowTemplate = {
  id: string;
  appName: string;
  /** Optional French counterpart of `appName`, for surfaces that want to show a French app
   *  name. Free text — unlike `appName` it is neither slug-formatted nor unique, because
   *  nothing routes or deep-links by it. Empty string / absent when the originator never set
   *  one, so always fall back to `appName`. Not versioned: it lives on the template row, so an
   *  edit takes effect immediately rather than on the next approval. */
  appNameFr?: string;
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  type: WorkflowType;
  platform: TargetPlatform;
  /** Maps to BMO's `appInfo.subsequentApplication` (save-application v8), sent on
   *  every save of an application built from this template. Set via the Create/Edit
   *  Workflow modal's "Advanced" panel. */
  subsequentApplication: boolean;
  /** Name of the "Download all" ZIP, in the filename-template token language
   *  (`{field:…}`, `{today:…}`, `{field:a|field:b}`) — e.g. `RDSP_{field:lastname1}`.
   *  Versioned: an advisor gets the published version's value. Null / absent = the
   *  bundle is named after its first document, as it always was. */
  packageFilenameTemplate?: string | null;
  /** Runtime date format. Versioned like `packageFilenameTemplate`. Null / absent =
   *  'DD/MM/YYYY', the format every workflow used before this setting existed. */
  dateFormat?: WorkflowDateFormat | null;
  /** Application Review layout. Versioned like `dateFormat`. */
  reviewLayout?: WorkflowReviewLayout | null;
  /** eSignature Summary shows every phone as (416) 970-8445. Versioned like `dateFormat`. */
  formatPhoneNumbers?: boolean | null;
  /** Originator-authored key/value pairs a field can prefill from, via
   *  `sourceValue: 'variable.<key>'`. Authored in the Create/Edit Workflow modal's "Advanced"
   *  panel.
   *
   *  NOT versioned — unlike `dateFormat` and friends above, these live on the template row
   *  (like `appNameFr` and `subsequentApplication`), so an edit reaches advisors on the
   *  already-published version without an approval. `templateAtVersion` therefore does NOT
   *  overlay them. Absent / empty on every workflow that defines none. */
  variables?: WorkflowVariable[];
  order: number;
  ownerOid: string;
  /** Display name of the creator at draft-creation time. Empty for legacy rows; FE falls back to ownerOid. */
  ownerName: string;
  status: WorkflowStatus;
  currentVersionNumber: number;
  /** Label of the active approved version (e.g. "v1.2"). Empty until first approval. */
  currentVersionLabel: string;
  /** The approved version this form was built from — sent on advisor reads only. Differs from
   *  `currentVersionLabel` when a submitted application is shown on the version it was filed on. */
  servedVersionLabel?: string | null;
  /** Label of the in-flight Draft/Pending/Rejected row (e.g. "v3"). Empty when the workflow is sitting on its published version. */
  inProgressVersionLabel: string;
  isActive: boolean;
  hasUnsubmittedChanges: boolean;
  createdAt: string;
  updatedAt: string;
  /** Ordered list of workflow steps. Always returned (empty array when there are none). */
  steps: WorkflowStep[];
  /** Number of steps on the template. Populated on EVERY workflow response, including
   *  the list endpoint where `steps` itself is suppressed — see `WorkflowSummary`.
   *
   *  NULL means UNKNOWN, not zero: a row persisted before the count was denormalized,
   *  read without its step graph. Treat unknown as "allow" — the BE's EnsureSubmittable
   *  is authoritative and still refuses a genuinely empty workflow, so the user gets an
   *  error on click rather than a permanently dead button. Such rows heal on next save. */
  stepCount: number | null;
  /** Live editor-lock state — null when nobody is editing or the previous lock has expired. */
  lock?: LockState | null;
  /** Newly-approved version awaiting the Approver's explicit promote. Set when
   *  Approve fires on top of an already-live published version (controlled
   *  rollout — v1 keeps serving until the Approver toggles publish on v2).
   *  Null/0 when no staged approval exists. */
  pendingApprovedVersionNumber?: number | null;
  /** Display label paired with `pendingApprovedVersionNumber` (e.g. "v2"). Empty when none. */
  pendingApprovedVersionLabel?: string;
  /** Timestamp of the most recent submit-for-approval action. Null on Drafts that
   *  have never been submitted and on legacy rows written before this field landed —
   *  the WorkflowCard hides the "Submitted" row in that case. */
  submittedAt?: string | null;
  /** When an approver archived this workflow; null while active. Orthogonal to
   *  `status` — an archived workflow keeps the Approved/Rejected status it held,
   *  which is what the Archive page shows alongside it. */
  archivedAt?: string | null;
  /** True when `archivedAt` is set. Sent explicitly so the FE never infers
   *  archived-ness from a timestamp's presence. */
  isArchived?: boolean;
  /** Optional dealer-code allow-list. When non-empty, an advisor only sees this
   *  application type if their own dealer code appears here. Combined with
   *  `excludedDealerCodes` via AND when both are non-empty. */
  includedDealerCodes: string[];
  /** Optional dealer-code deny-list — the inverse of `includedDealerCodes`. */
  excludedDealerCodes: string[];
};

/**
 * What the list endpoint (`GET /workflows`) actually returns: every card field, but
 * NO `steps`. The step graph carries all fields, PDF entries and conditions on a
 * workflow and dwarfs the metadata the dashboards render, so the BE suppresses it
 * there and sends `stepCount` instead.
 *
 * This is a distinct type rather than `WorkflowTemplate` with an empty array on
 * purpose: omitting the key makes `w.steps` a COMPILE error on any list-fed view,
 * instead of a silently-empty array that reads as "this workflow has no steps".
 * Anything that genuinely needs steps must fetch the single template via
 * `workflowsApi.get(id)`.
 *
 * A full `WorkflowTemplate` remains assignable to `WorkflowSummary`, so write-through
 * responses from create / patch / cancel-pending can be patched straight into list state.
 */
export type WorkflowSummary = Omit<WorkflowTemplate, 'steps'>;

/**
 * One row in a Canada Post AddressComplete Find response. `next` tells
 * the FE whether picking this row drills further (call Find again with
 * the row's `id` as `lastId`) or completes (call Retrieve with the id).
 */
export type AddressSuggestion = {
  id: string;
  text: string;
  description: string;
  highlight: string;
  cursor: string;
  next: 'Find' | 'Retrieve' | string;
};

/** ZeroBounce-normalized status. `Invalid` is the only value that blocks
 *  Next per INFLOWS-188 AC; everything else is either "definitely fine"
 *  (Valid), "we can't tell" (CatchAll/Unknown), or "reachable but should
 *  not send" (SpamTrap/DoNotMail — surface as warning but don't block). */
export type EmailVerifyStatus =
  'Valid' | 'Invalid' | 'CatchAll' | 'SpamTrap' | 'DoNotMail' | 'Unknown';

/** Per-email verification result echoed by the /integrations/email/verify
 *  endpoint. `didYouMean` is the typo suggestion (e.g. `gmial.com` →
 *  `gmail.com`) — worth surfacing as a warning even on a Valid result. */
export type EmailVerifyResult = {
  email: string;
  status: EmailVerifyStatus;
  subStatus: string | null;
  didYouMean: string | null;
};

/** Full structured address returned by Retrieve. Field names mirror the
 *  BE response; the consumer maps onto sibling form fields. */
export type AddressDetails = {
  line1: string;
  line2: string;
  /** Structured unit / apartment number (e.g. "232" for the address
   *  "232-46 Glamis Green SW"). The BE strips the matching "{subBuilding}-"
   *  prefix from line1 so writing subBuilding to the apartment field and
   *  line1 to the street field doesn't duplicate the unit. */
  subBuilding: string;
  city: string;
  provinceCode: string;
  provinceName: string;
  postalCode: string;
  countryCode: string;
  countryName: string;
};

// Orphaned as of the Phase 9 audit work (see recentDecisions() below). Kept commented for a
// future recent-decisions view; the backend /workflows/recent-decisions still returns this shape.
// /** One row per Approved/Rejected version for the approver dashboard. */
// export type WorkflowDecision = {
//   templateId: string;
//   appName: string;
//   nameEn: string;
//   nameFr: string;
//   versionNumber: number;
//   majorVersion: number;
//   minorVersion: number;
//   /** "v{major}.{minor}" (or "v{major}" when minor == 0). Authoritative for display. */
//   versionLabel: string;
//   status: WorkflowStatus;
//   // Approve / Reject are stored separately. Only one side is populated per row,
//   // matching the row's terminal status — never both at once.
//   approvedBy?: string | null;
//   approvedByName?: string | null;
//   /** Approver email snapshotted at decision time. Preferred over the name on the
//    *  Audit History page; null on legacy rows decided before email capture. */
//   approvedByEmail?: string | null;
//   approvedAt?: string | null;
//   rejectedBy?: string | null;
//   rejectedByName?: string | null;
//   /** Rejecter email snapshotted at decision time. See {@link approvedByEmail}. */
//   rejectedByEmail?: string | null;
//   rejectedAt?: string | null;
//   rejectionComment?: string | null;
//   /** Whether THIS version is the currently-live published row. True → renders
//    *  as "Published"; false → "Approved" (matches the dashboard's Approved
//    *  Workflows tab). Always false for Rejected decisions. */
//   isActive: boolean;
// };

/**
 * One row in the Audit History viewer. Unlike {@link WorkflowDecision} this covers EVERY
 * state change (create / submit / reject / approve / clone / reorder / …), sourced from the
 * backend AuditEvents table rather than decided version rows.
 */
export type AuditLogEntry = {
  actorOid: string;
  actorRole: string;
  /** Dotted action name, e.g. "workflow.submitted" / "workflow.approved". */
  action: string;
  entityType: string;
  entityId: string;
  /** Human-friendly entity name (workflow app name / custom-element name) snapshotted at
   *  emit time. Null on legacy rows written before the field landed — the FE falls back
   *  to `entityId` in that case. */
  entityName?: string | null;
  before?: string | null;
  after?: string | null;
  /** ISO-8601 timestamp of when the action occurred. */
  at: string;
  /** Display name snapshotted from the caller's JWT at emit time (SFB-05 AC3).
   *  Null on legacy rows written before the field landed — the FE falls back
   *  to `actorOid` in that case. */
  actorName?: string | null;
  /** Email snapshotted from the caller's JWT. Same null semantics as name. */
  actorEmail?: string | null;
};

export type WorkflowVersion = {
  templateId: string;
  versionNumber: number;
  majorVersion: number;
  minorVersion: number;
  /** "v{major}.{minor}" (or "v{major}" when minor == 0). */
  versionLabel: string;
  status: WorkflowStatus;
  createdBy: string;
  createdAt: string;
  approvedBy?: string | null;
  approvedByName?: string | null;
  /** Approver's email at approval time. Snapshotted alongside {@link approvedByName};
   *  BMO uses it on the versions history page for the AC3 audit ("who published this"). */
  approvedByEmail?: string | null;
  approvedAt?: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  rejectedByEmail?: string | null;
  rejectedAt?: string | null;
  rejectionComment?: string | null;
  /** Person who first flipped this version live (SetActive true or PromotePendingApproval).
   *  Distinct from approvedBy — approval and publish are separate steps in the
   *  controlled-rollout model. Legacy rows written before the split will have null
   *  even when live; FE falls back to the approver's identity in that case. */
  publishedBy?: string | null;
  publishedByName?: string | null;
  publishedByEmail?: string | null;
  publishedAt?: string | null;
  isActive: boolean;
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  type: WorkflowType;
  platform: TargetPlatform;
  order: number;
  /** Source label (e.g., "v1") when this draft was forked from a non-current archived version. Null/absent otherwise. */
  forkedFromLabel?: string | null;
  /** Frozen with the version — see `WorkflowTemplate.packageFilenameTemplate`. */
  packageFilenameTemplate?: string | null;
  /** Frozen with the version — see `WorkflowTemplate.dateFormat`. */
  dateFormat?: WorkflowDateFormat | null;
  /** Frozen with the version — see `WorkflowTemplate.reviewLayout`. */
  reviewLayout?: WorkflowReviewLayout | null;
  /** Frozen with the version — see `WorkflowTemplate.formatPhoneNumbers`. */
  formatPhoneNumbers?: boolean | null;
  /** Dealer-code allow-list frozen with this version — see
   *  `WorkflowTemplate.includedDealerCodes`.
   *
   *  `null` / absent means this row PREDATES the per-version freeze (2026-10-26) and
   *  cannot say what gating it shipped with — distinct from `[]`, which means it
   *  genuinely shipped ungated. Views must not collapse the two: show the template's
   *  live lists with a caveat for null, and the version's own for `[]`. */
  includedDealerCodes?: string[] | null;
  /** Dealer-code deny-list frozen with this version. Same null-vs-empty contract as
   *  `includedDealerCodes`. */
  excludedDealerCodes?: string[] | null;
  /** Frozen snapshot of this version's step list as it was at submit / approval
   *  time. Lets the approver / originator preview an archived version without
   *  seeing the template's current state leak in.
   *
   *  Only present on the SINGLE-version endpoint (`workflowsApi.getVersion`). The
   *  version list omits it — see {@link WorkflowVersionSummary}. */
  steps: WorkflowStep[];
};

/**
 * What `workflowsApi.listVersions()` returns: every version row's metadata, but NO
 * frozen step snapshot. Snapshots live in their own table on the BE, and a versions
 * list would otherwise carry every snapshot a workflow has ever had just to render a
 * table of labels, statuses and audit stamps.
 *
 * Distinct type rather than an empty array, for the same reason as {@link WorkflowSummary}:
 * `v.steps` on a list-fed view is a COMPILE error instead of a silently-empty list that
 * would render a version as having no content. Fetch the one version you need to render
 * via `workflowsApi.getVersion(id, versionNumber)`.
 */
export type WorkflowVersionSummary = Omit<WorkflowVersion, 'steps'>;

export type WorkflowFormData = Omit<
  WorkflowTemplate,
  | 'id'
  | 'ownerOid'
  | 'ownerName'
  | 'lock'
  | 'status'
  | 'currentVersionNumber'
  | 'currentVersionLabel'
  | 'inProgressVersionLabel'
  | 'isActive'
  | 'hasUnsubmittedChanges'
  | 'createdAt'
  | 'updatedAt'
  | 'nameFr'
  | 'descriptionEn'
  | 'descriptionFr'
  | 'steps'
  // Server-derived, like the version/lock fields above — never part of the
  // create/edit form payload.
  | 'stepCount'
> & {
  // English title is mandatory; French title + descriptions are optional (ADR-0016).
  nameFr?: string;
  descriptionEn?: string;
  descriptionFr?: string;
};

/**
 * The workflow as a specific version froze it, for previewing / reviewing that version: the
 * version's steps and its runtime settings over the template's operational fields. Every
 * "show me version N" surface goes through this, so a versioned property added later cannot be
 * forgotten at one of them — a preview that kept the working copy's settings would show the
 * approver a form advisors will never get.
 */
export function templateAtVersion(
  template: WorkflowTemplate,
  version: WorkflowVersion,
): WorkflowTemplate {
  return {
    ...template,
    steps: version.steps ?? [],
    packageFilenameTemplate: version.packageFilenameTemplate ?? null,
    dateFormat: version.dateFormat ?? null,
    reviewLayout: version.reviewLayout ?? null,
    formatPhoneNumbers: version.formatPhoneNumbers ?? null,
    // `variables` is deliberately absent. It is live configuration rather than versioned
    // content — no version snapshot carries it — so the template's own value, already kept by
    // the spread above, IS the correct one for every version. Adding a line for it here would
    // blank the variables on every "show me version N" surface.
  };
}

/**
 * Every step carrying originator-uploaded PDF source templates.
 *
 * Keys off the DATA rather than the step type on purpose. Those templates lived on
 * 'PdfFileUpload' until the 2026-08-08 merge moved them to 'PdfGeneration', and four
 * separate call sites were each testing the type by hand — the PDF picker, the
 * field-mapping viewer, the approver's read-only drawer, and `collectPdfDocs`, which
 * is what actually produces the advisor's filled PDFs. A missed one there is silent:
 * the workflow simply generates nothing.
 *
 * The domain only ever populates `fileEntries` on the PDF step, so this is exact,
 * and it survives both the rename and a stale bundle meeting a folded payload.
 */
export function stepsWithPdfTemplates(template: WorkflowTemplate): WorkflowStep[] {
  return (template.steps ?? []).filter((s) => (s.fileEntries?.length ?? 0) > 0);
}

/**
 * The step's label for the surfaces that LIST it: the runtime rail and the Application Review
 * recap. Falls back to the step title — see {@link WorkflowStep.navTitleEn}.
 *
 * Two fallbacks, in this order, and the order matters:
 *
 *   1. Per-language, within whichever pair is in play: the requested locale, then the other one.
 *      This is the same rule the title has always followed, so an originator who fills in only
 *      the English nav title gets it in both locales rather than a blank rail row.
 *   2. Pair to pair: no usable nav title at all falls through to the title.
 *
 * Collapsing those into one `||` chain over four strings looks equivalent and is not — it would
 * let a French TITLE win over an English NAV title, so a step named "DCA" in the rail would read
 * "BMO Mutual Funds Dollar Averaging (DCA)" the moment the advisor switched to French.
 *
 * Call this rather than reading the fields; three runtimes render the same rail, and an inline
 * copy of the rule in any of them is a copy that drifts.
 */
export function stepNavTitle(
  step: Pick<WorkflowStep, 'titleEn' | 'titleFr' | 'navTitleEn' | 'navTitleFr'>,
  language: 'en' | 'fr',
): string {
  const pick = (en: string | null | undefined, fr: string | null | undefined) =>
    (language === 'fr' && fr ? fr : en) || en || fr || '';
  return pick(step.navTitleEn, step.navTitleFr) || pick(step.titleEn, step.titleFr);
}

/**
 * One file's malware scan verdict as the API reports it (ADR-0024).
 *
 * `status` deliberately stays a plain string rather than the `FileState` union: an older bundle
 * meeting a newer server must be able to receive a status it does not recognise without a type
 * error, and the poller treats anything unrecognised as "keep waiting" rather than as a pass.
 */
export type AdditionalDocumentScanStatus = {
  key: string;
  /** `scanning` | `clean` | `infected` | `blocked` | `missing`. */
  status: string;
  /** Raw scanner reason behind a `blocked`, e.g. `UNSUPPORTED`. Never a threat name. */
  detail?: string | null;
};
